import * as React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import { ExportableArray } from "@impero-com/common/utils/export";
import { set, clone } from "@impero-com/common/utils/obj";
import { KeyTrap } from "@impero-com/ui";
import { range } from "lodash-es";
import * as Papa from "papaparse";
import { createSelector } from "reselect";

import { copyDataToClipboard } from "utils/clipboard";
import {
    ValidationError,
    ValidationErrors,
    ErrorMapping,
} from "utils/validator";

import { RowErrorGroup, TableErrorList } from "./errorList";
import {
    StandardSelectionHandler,
    EditCellSelectionHandler,
    NoopSelectionHandler,
    SelectionHandlerProps,
    SelectionHandlerStyles,
} from "./selection";
import * as theme from "./theme.css";
import { OrderBy, CellKeyboardEventType } from "./types";
import type {
    FixedWidthColumn,
    MinWidthColumn,
    Column,
    Editor,
    EditorProps,
    Blurable,
    CellKeyboardEvent,
    RawSelectionDescriptor,
} from "./types";

// A visible column may not have less than this width, if neither minWidth nor fixedWidth are set on it.
const MIN_WIDTH_PER_COLUMN = 50;

/*
 * Note: the table component maintains a sorted copy of the data it gets injected.
 * This means a mismatch between row numbers in the original data, and row
 * numbers as displayed. All callbacks injected into the table component use
 * actual (ie, from the original data) row numbers. Per default, all row number
 * variables in the table component and sub-components should be actual row
 * numbers, and row numbers corresponding to the sorted data should be
 * explicitly marked as such, with a 'sorted' in the name.
 */

function SpecCharsCSSEscape(name: string): string {
    return name.replace(/:/g, "-").replace(/ /g, "_");
}

// Width of the row-controls column, if any
const ROW_CONTROLS_WIDTH_IN_PX = 50;

// XXX If it's not 18 then IE won't call onScroll when clicking the arrows *sigh*.
// The CSS reflects this value.
const SCROLLBAR_V_WIDTH = 18;
const RIGHT_SIDE_BORDER_WIDTH = 0;

/**
 * A tr tag-equivalent, rendered as a `div` with a `tr` class.
 *
 * @param {Object} props:
 * - `styles` (TrStyles): the set of the CSS classes to use.
 * - `children` (string or React nodes, optional): child nodes.
 *
 * @return {Tr} a React node.
 */
function Tr(props: TrProps) {
    const classNames: Array<string> = [theme.tr];
    if (props.styles.rowClassName) {
        classNames.push(props.styles.rowClassName);
    }
    const className = classNames.join(" ");
    return <div className={className}>{props.children}</div>;
}

interface TrStyles {
    rowClassName: string;
}

interface TrProps {
    styles: TrStyles;
    children?: React.ReactNode;
}

/**
 * A th tag-equivalent, rendered as a `div` with a `th` class.
 *
 * @param {Object} props:
 * - `styles` (ThStyles): the set of the CSS classes to use.
 * - `children` (string or React nodes, optional): child nodes.
 * - `onClick` (function, optional): should accept a click event.
 *
 * @return {Th} a React node.
 */
function Th(props: ThProps) {
    const classNames: Array<string> = [theme.th];
    if (props.styles.headerCellClassName) {
        classNames.push(props.styles.headerCellClassName);
    }
    const className = classNames.join(" ");
    return (
        <div
            className={className}
            onClick={props.onClick}
            title={props.tooltip}
        >
            <div className={theme.thInner}>{props.children}</div>
        </div>
    );
}

interface ThStyles {
    headerCellClassName: string;
}

interface ThProps {
    styles: ThStyles;
    onClick?: React.MouseEventHandler<HTMLElement>;
    tooltip?: string;
    children?: React.ReactNode;
}

/**
 * A thead tag-equivalent, rendered as a `div` with a `thead` class.
 *
 * @param {Object} props:
 * - `styles` (TheadStyles): the set of the CSS classes to use.
 * - `children` (string or React nodes, optional): child nodes.
 *
 * @return {Thead} a React node.
 */
function Thead(props: TheadProps) {
    const classNames: Array<string> = [theme.thead];
    if (props.styles.headerClassName) {
        classNames.push(props.styles.headerClassName);
    }
    const className = classNames.join(" ");
    return <div className={className}>{props.children}</div>;
}

interface TheadStyles {
    headerClassName: string;
}

interface TheadProps {
    styles: TheadStyles;
    children?: React.ReactNode;
}

/**
 * A td tag-equivalent, renders as a `div` with a `td` class.
 *
 * @param {Object} props:
 * - `styles` (TdStyles): the set of the CSS classes to use.
 * - `children` (string or React nodes, optional): child nodes.
 * - `onClick` (function, optional): should accept a click event.
 *
 * @return {Td} a React node.
 */
class Td extends React.Component<TdProps> {
    private td: React.RefObject<HTMLDivElement | null>;
    constructor(props: TdProps) {
        super(props);
        this.td = React.createRef();
    }
    componentDidUpdate() {
        if (this.props.isFocused && this.td.current) {
            const inputs = this.td.current.getElementsByTagName("input");
            if (inputs.length > 0) {
                inputs[0].focus();
            }
        }
    }
    render() {
        const { cellClassName } = this.props;
        const classNames: Array<string> = [theme.td];
        if (cellClassName) {
            classNames.push(cellClassName);
        }
        const className = classNames.join(" ");
        return (
            <div
                title={this.props.title}
                className={className}
                onClick={this.props.onClick}
                ref={this.td}
            >
                {this.props.children}
            </div>
        );
    }
}

interface TdStyles {
    cellClassName: string;
}

interface TdProps {
    isFocused?: boolean;
    title?: string;
    cellClassName: string;
    onClick?: React.MouseEventHandler<HTMLElement>;
    children?: React.ReactNode;
}

/**
 * A sortable th header. Displays an up or down arrow if the column is sorted. Renders as a @{link Th} tag.
 *
 * Props:
 * - all `Th` props
 * - `orderBy` (string, optional): the sort order of the header (one of `"ASC"` or `"DESC"`). Determines the sense of the arrow. If set to any other value, the arrow is not displayed.
 * - `onSort` (function, optional): a callback invoked when the header is clicked. Should accept an orderBy parameter (one of `"ASC"` or `"DESC"`).
 */
class SortableTh extends React.Component<SortableThProps> {
    render() {
        const sortableClassName = this.props.onSort ? theme.sortable : "";

        let arrow: React.ReactNode;
        if (this.props.orderBy === OrderBy.ASC) {
            arrow = <div className={theme.arrowDown} />;
        } else if (this.props.orderBy === OrderBy.DESC) {
            arrow = <div className={theme.arrowUp} />;
        } else {
            arrow = null;
        }
        const handleClick = this.props.onSort
            ? () => this.handleClick()
            : undefined;
        const content = this.props.children ? this.props.children : "\xa0";
        return (
            <Th
                styles={{
                    headerCellClassName:
                        this.props.styles.headerCellClassName +
                        " " +
                        sortableClassName,
                }}
                onClick={handleClick}
                tooltip={typeof content === "string" ? content : undefined}
            >
                {arrow}
                <div className={theme.sortableLabel}>{content}</div>
            </Th>
        );
    }
    handleClick() {
        const oldOrderBy = this.props.orderBy;
        let newOrderBy: OrderBy;
        if (oldOrderBy === OrderBy.ASC) {
            newOrderBy = OrderBy.DESC;
        } else {
            newOrderBy = OrderBy.ASC;
        }
        this.props.onSort!(newOrderBy);
    }
}

interface SortableThProps extends ThProps {
    orderBy?: OrderBy;
    onSort?: (sortDirection: OrderBy) => void;
}

/**
 * Smart table header, generates a {@link Thead} tag and its content.
 *
 * Props:
 * - `columns` (array of objects, required): a list of columns to display. See the {@link Table} class for the expected format.
 * - `withRowControlColumn` (boolean, optional): if set to `true`, the column with row numbers will be displayed.
 * - `sortColumn` (string, optional): the name of the column used for sorting. It will be displayed with an arrow.
 * - `orderBy` (string, optional): should be one of `"ASC"` or `"DESC"`. Determines the direction of the arrow (if `sortColumn` is set).
 * - `onSort` (function, optional): callback invoked when a column header is clicked. See {@link SortableTh} for the signature.
 */
class Theader<Row> extends React.PureComponent<TheaderProps<Row>> {
    render() {
        const { columns, withRowControlColumn, styles } = this.props;
        const maybeRowControlColumn = withRowControlColumn
            ? this.buildRowControlColumn(styles)
            : null;
        return (
            <Thead styles={styles}>
                <Tr styles={{ rowClassName: styles.headerRowClassName }}>
                    {maybeRowControlColumn}
                    {columns.map((col, index) => {
                        const onSortProp = this.props.onSort;
                        const onSort =
                            onSortProp && col.label !== ""
                                ? (orderBy: OrderBy) =>
                                      onSortProp(col.name, orderBy)
                                : undefined;
                        const orderBy =
                            this.props.sortColumn === col.name
                                ? this.props.orderBy
                                : undefined;
                        const key = "Col" + index;
                        const thClassNames = [
                            "col-" + SpecCharsCSSEscape(col.name),
                        ];
                        if (styles.headerCellClassName) {
                            thClassNames.push(styles.headerCellClassName);
                        }
                        if (col.headerCellClassName) {
                            thClassNames.push(col.headerCellClassName);
                        }
                        if (index === columns.length - 1) {
                            thClassNames.push(theme.thLast);
                        }
                        const thClassName = thClassNames.join(" ");
                        return (
                            <SortableTh
                                key={key}
                                orderBy={orderBy}
                                onSort={onSort}
                                styles={{ headerCellClassName: thClassName }}
                            >
                                {col.headerWidget ? (
                                    col.headerWidget
                                ) : (
                                    <span>{addLinebreaks(col.label, key)}</span>
                                )}
                            </SortableTh>
                        );
                    })}
                    <Th
                        styles={{
                            headerCellClassName:
                                styles.headerCellClassName +
                                " " +
                                theme.headerScrollbarCell,
                        }}
                    />
                </Tr>
            </Thead>
        );
    }
    private buildRowControlColumn(styles: TheaderStyles) {
        return (
            <Th
                styles={{
                    headerCellClassName:
                        styles.headerCellClassName +
                        " " +
                        theme.rowControlColumn,
                }}
            >
                &nbsp;
            </Th>
        );
    }
}

type TheaderStyles = TheadStyles & {
    headerRowClassName: string;
} & ThStyles;

interface TheaderProps<Row> {
    columns: Array<Column<Row, any, any, any>>;
    withRowControlColumn: boolean;
    styles: TheaderStyles;
    sortColumn: string | undefined;
    orderBy: OrderBy;
    onSort: ((colName: string, sortDirection: OrderBy) => void) | null;
}

/**
 * Returns a {@link Trow}. Accepts a hash of named parameters with the same values as
 * `Trow`'s props.
 *
 * @returns {array} an array containing a single `Trow` element (since a single row of data may be rendered as multiple <tr>).
 */
function defaultRowRenderer<Row>({
    key,
    id,
    row,
    columns,
    rowNumber,
    sortedRowNumber,
    rowErrors,
    rowCellErrors,
    errorMapping,
    styles,
    getCellClassName,
    selection,
    onCellClick,
    onCellChanged,
    onCellPaste,
    onHotkey,
    onBlur,
    onRowSelected,
    rawData,
    withRowControlColumn,
}: RowRendererParams<Row>): Array<React.ReactNode> {
    return [
        <Trow
            id={id}
            key={key}
            columns={columns}
            row={row}
            rowNumber={rowNumber}
            sortedRowNumber={sortedRowNumber}
            rowErrors={rowErrors}
            rowCellErrors={rowCellErrors}
            withRowControlColumn={withRowControlColumn}
            errorMapping={errorMapping}
            styles={styles}
            getCellClassName={getCellClassName}
            selection={selection}
            onCellClick={onCellClick}
            onCellChanged={onCellChanged}
            onCellPaste={onCellPaste}
            onRowSelected={onRowSelected}
            onHotkey={onHotkey}
            onBlur={onBlur}
            rawData={rawData}
        />,
    ];
}
interface RowRendererParams<Row> extends TrowProps<Row> {
    key: string;
}

type RowRenderer<Row> = (
    props: RowRendererParams<Row>
) => Array<React.ReactNode>;

/**
 * The body of the table. Renders as a `div` with a `tbody` class.
 *
 * Props:
 * - `columns` (array of objects, required): a list of columns to display. See the {@link Table} class for the expected format.
 * - `rows` (array of objects, required): a list of rows to display. See the {@link Table} class for the expected format.
 * - `getCellClassName` (function, optional): a function returning the class of a cell. Should take a column name and a row number. Mostly useful for the selection handler.
 * - `tableId` (string, required): the id of the table.
 * - `errors` (map of id => error, optional): maps an id (id of a row, of the shape "$id_of_table[$rowIndex]", or id of a cell, of the shape "$rowId$colName") to an error.
 * - `errorMapping` (map of error type => message, required if `errors` is
 * present) : maps an error type to a message.
 * - `selection` (RawSelectionDescriptor, optional): the selection descriptor (for editable tables).
 * - `onCellClick` (function, optional): function called when a cell is clicked. Should take the name of the column and the row number. Used by the selection handler.
 * - `onCellChanged` (function, optional): function called when the content of the cell has been changed (for editable tables). Only invoked if the data has actually changed. Should take the column name, the row number, the new data and the old row (with the old version of the data).
 * - `onCellPaste` (function, optional): function called when the user pasted data in a cell while the cell was the active cell and the mode was 'focus'. Should take the column name, the row number and the clipboard data (of type `DataTransfer`, see https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer).
 * - `onNewRowCellChanged` (function, optional): if present, an empty row will be displayed at the bottom of the table. The function will be called when a cell of this empty row has been edited.
 * - `onRowSelected` (function, required if `editable` is true): function called when the user clicked on the column containing the row numbers. Should accept a row number.
 * - `buildEmptyRow` (function, mandatory if `onNewRowCellChanged` is present): a function which returns an empty row (this is necessary as the table relies on the `getData()` function of each column and has no knowledge of the structure of individual rows).
 * - `onHotkey` (function, required): a function called when a hotkey has been pressed (keyup event, as we need arrow events, which only register on keyup) on a cell. Currently called for 'Enter' when a cell is in edit mode, and for arrow events when a cell is in focus mode. Should take the keyboard event. Note that 'Shift-Enter' does *not* trigger `onHotkey` and can be used for creating multi-line text in a textarea.
 * - `onBlur` (function, mandatory if the table is editable): a function called when the cell was in focus mode and the user clicked elsewhere.
 * - `rowRenderer` (function, required): a function which, given the same props as `Trow`, will return an array of React elements to render. Should take a hash of named parameters identical to `Trow`s props, and return an array of React elements (this makes it possible to render several rows for a given data row, which is used in the collect screen's data sheet task). These elements should be `div` with a `tr` class. If alignment with existing columns is needed, use the class `col-$colname` to build a cell of the same width as the column with the name $colname.
 * - `maxVisibleRows` (number, optional): if present, only up to `maxVisibleRows` rows are displayed at a time, and a scrollbar lets the user move up and down.
 * - `sortedRowNumberToActualRowNumber` (array, defaults to null): a list of before-sorting row numbers (from the `rows` props), in the current sorting order (eg, if rows [ ["a"], ["b"], ["c"] ] are currently sorted as [ ["b"], ["a"], ["c"] ], would be equal to [1, 0, 2]). Is null if rows are not sorted.
 * - `actualRowNumberToSortedRowNumber` (array, defaults to null): the invert of `sortedRowNumberToActualRowNumber`
 * - `getRowKey` (function, optional): if present, is used to generate the React key of the row.
 *
 * State:
 * - `firstSortedRowIndex`: the (sorted) index of the first row being displayed (when using maxVisibleRows)
 * - `lastSortedRowIndex`: the (sorted) index of the last row being displayed (when using maxVisibleRows)
 */
class Tbody<Row> extends React.Component<TbodyProps<Row>, TbodyState> {
    private tbody: React.RefObject<HTMLDivElement | null>;
    private wrapper: React.RefObject<HTMLDivElement | null>;
    private style: React.RefObject<HTMLStyleElement | null>;
    private verticalScroll: React.RefObject<HTMLDivElement | null>;
    private theadLeft: number;
    private verticalScrollContentHeight: number;
    private wrapperMinHeight: number;
    private wrapperTargetHeight: number;
    constructor(props: TbodyProps<Row>) {
        super(props);
        this.tbody = React.createRef();
        this.wrapper = React.createRef();
        this.style = React.createRef();
        this.verticalScroll = React.createRef();
        this.theadLeft = 0;
        this.verticalScrollContentHeight = 0;
        const firstSortedRowIndex = 0;
        const lastSortedRowIndex = this.getLastSortedRowIndex(
            firstSortedRowIndex,
            props
        );
        this.wrapperMinHeight = 0;
        this.wrapperTargetHeight = 0;
        this.state = {
            firstSortedRowIndex,
            lastSortedRowIndex,
            scrollHorizontally: false,
        };
    }
    private getLastSortedRowIndex(
        firstSortedRowIndex: number,
        props: TbodyProps<Row>
    ): number {
        const { maxVisibleRows } = props;
        const totalRowCount = this.getTotalRowCountWithEmptyRow(props);
        return maxVisibleRows
            ? Math.min(
                  firstSortedRowIndex + maxVisibleRows - 1,
                  totalRowCount - 1
              )
            : totalRowCount - 1;
    }
    private getFirstSortedRowIndex(lastSortedRowIndex: number): number {
        const { maxVisibleRows } = this.props;
        return maxVisibleRows
            ? Math.max(lastSortedRowIndex - (maxVisibleRows - 1), 0)
            : 0;
    }
    private getTotalRowCountWithEmptyRow(props: TbodyProps<Row>): number {
        const { rows, onNewRowCellChanged } = props;
        return onNewRowCellChanged ? rows.length + 1 : rows.length;
    }
    setScrollHorizontally(scrollHorizontally: boolean) {
        if (this.state.scrollHorizontally !== scrollHorizontally) {
            this.setState({
                scrollHorizontally,
            });
        }
    }
    updateHeight(totalRowCount: number): void {
        const { maxVisibleRows, verticalScrollHeight } = this.props;
        if (!maxVisibleRows) {
            return;
        }
        // Update the height of the containers when the data changes
        const totalHeight =
            (verticalScrollHeight * totalRowCount) / maxVisibleRows;
        // Use ceil here and floor/round in the scroll handler to make sure the last row can be displayed
        this.verticalScrollContentHeight = Math.ceil(totalHeight);
        this.updateStylesContent();
        const spring = () => {
            if (!this.wrapper.current) {
                return;
            }
            const currentHeight = Array.from(this.wrapper.current.children)
                .map((child) => child.clientHeight || 0)
                .reduce((memo, height) => memo + height, 0);
            if (currentHeight > this.wrapperMinHeight) {
                this.wrapperMinHeight = currentHeight;
                this.updateStylesContent();
            } else if (currentHeight < this.wrapperMinHeight) {
                this.wrapperTargetHeight = currentHeight;
                setTimeout(() => {
                    if (this.wrapperMinHeight > this.wrapperTargetHeight) {
                        //this.wrapperMinHeight = Math.max(this.wrapperTargetHeight, this.wrapperMinHeight - 10);
                        this.wrapperMinHeight = Math.max(
                            this.wrapperTargetHeight,
                            this.wrapperMinHeight -
                                Math.max(
                                    10,
                                    (this.wrapperMinHeight -
                                        this.wrapperTargetHeight) /
                                        2
                                )
                        );
                        this.updateStylesContent();
                        window.requestAnimationFrame(spring);
                    }
                }, 20);
            }
        };
        window.requestAnimationFrame(spring);
    }
    componentWillMount() {
        const firstSortedRowIndex = 0;
        const lastSortedRowIndex = this.getLastSortedRowIndex(
            firstSortedRowIndex,
            this.props
        );
        this.setState({ firstSortedRowIndex, lastSortedRowIndex });
    }
    componentDidMount() {
        const totalRowCount = this.getTotalRowCountWithEmptyRow(this.props);
        this.updateHeight(totalRowCount);
    }
    componentWillReceiveProps(nextProps: TbodyProps<Row>) {
        let firstSortedRowIndex = this.state.firstSortedRowIndex;
        let lastSortedRowIndex = this.state.lastSortedRowIndex;
        const totalRowCount = this.getTotalRowCountWithEmptyRow(nextProps);
        if (this.props.rows.length !== nextProps.rows.length) {
            firstSortedRowIndex = Math.min(
                firstSortedRowIndex,
                nextProps.rows.length
            );
            if (
                nextProps.maxVisibleRows &&
                nextProps.rows.length - firstSortedRowIndex <
                    nextProps.maxVisibleRows
            ) {
                firstSortedRowIndex = Math.max(
                    0,
                    nextProps.rows.length - nextProps.maxVisibleRows
                );
            }
            lastSortedRowIndex = this.getLastSortedRowIndex(
                firstSortedRowIndex,
                nextProps
            );
            if (
                nextProps.maxVisibleRows &&
                nextProps.maxVisibleRows < totalRowCount &&
                firstSortedRowIndex > 0 &&
                1 + lastSortedRowIndex - firstSortedRowIndex <
                    nextProps.maxVisibleRows
            ) {
                // This dodgy-looking code attempts to solve the following issue:
                // If we use lazy-loading and display the last page of the
                // table, and one row disappears, firstSortedRowIndex will not
                // change. However, lastSortedRowIndex will be reduced by one, which
                // means we'll display a number of rows < maxVisibleRows. In
                // this case, we need to display one additional row at the top
                // (by decrementing firstSortedRowIndex). In all other cases, we
                // append/remove rows at the bottom of the current page.
                if (lastSortedRowIndex === totalRowCount - 1) {
                    firstSortedRowIndex--;
                }
            } else if (
                nextProps.maxVisibleRows &&
                totalRowCount < nextProps.maxVisibleRows
            ) {
                // If we can display all the rows at once, ensure we do
                firstSortedRowIndex = 0;
                lastSortedRowIndex = this.getLastSortedRowIndex(
                    firstSortedRowIndex,
                    nextProps
                );
            }
        }
        if (nextProps.selection) {
            if (
                !this.props.selection ||
                this.props.selection.row.rawRowNumber !==
                    nextProps.selection.row.rawRowNumber
            ) {
                // If the props changed because the user moved the cursor,
                // change the view in consequence
                const newActiveSortedCellRowNumber =
                    this.getActiveSortedCellRowNumber(
                        nextProps.selection,
                        nextProps
                    );
                const isActiveCellVisible =
                    firstSortedRowIndex <= newActiveSortedCellRowNumber &&
                    newActiveSortedCellRowNumber <= lastSortedRowIndex;
                if (!isActiveCellVisible) {
                    if (
                        newActiveSortedCellRowNumber <
                        this.state.firstSortedRowIndex
                    ) {
                        firstSortedRowIndex = newActiveSortedCellRowNumber;
                        lastSortedRowIndex = this.getLastSortedRowIndex(
                            firstSortedRowIndex,
                            nextProps
                        );
                    } else {
                        lastSortedRowIndex = newActiveSortedCellRowNumber;
                        firstSortedRowIndex =
                            this.getFirstSortedRowIndex(lastSortedRowIndex);
                    }
                }
            }
        }
        if (nextProps.maxVisibleRows && this.verticalScroll.current) {
            // Do not set the state directly, make the vertical scrollbar rule the position
            this.verticalScroll.current.scrollTop = Math.ceil(
                (firstSortedRowIndex * this.verticalScrollContentHeight) /
                    totalRowCount
            );
            // Update height now: the indexes depend on
            // verticalScrollContentHeight, and they need to be correct before
            // render()
            this.updateHeight(totalRowCount);
            this.updateSortedRowIndexes(nextProps);
        } else {
            this.setState({ firstSortedRowIndex, lastSortedRowIndex });
        }
    }
    private handleHorizontalScroll(e: React.UIEvent<HTMLElement>): void {
        e.preventDefault();
        if (!this.wrapper.current) {
            return;
        }
        this.theadLeft = -this.wrapper.current.scrollLeft || 0;
        this.updateStylesContent();
    }
    private handleVerticalScroll(e: React.UIEvent<HTMLElement>): void {
        e.preventDefault();
        this.updateSortedRowIndexes(this.props);
    }
    private handleWheel(e: React.WheelEvent<HTMLElement>) {
        if (!this.verticalScroll.current || !this.props.maxVisibleRows) {
            return;
        }
        // Do not prevent default or you won't be able to scroll horizontally
        const deltaY = e.deltaY;
        if (deltaY) {
            const deltaMode = e.deltaMode;
            e.preventDefault();
            let scrollY: number;
            if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
                const totalRowCount = this.getTotalRowCountWithEmptyRow(
                    this.props
                );
                scrollY = Math.floor(
                    (deltaY * this.verticalScrollContentHeight) / totalRowCount
                );
            } else if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
                const totalRowCount = this.getTotalRowCountWithEmptyRow(
                    this.props
                );
                scrollY = Math.floor(
                    (deltaY *
                        this.props.maxVisibleRows *
                        this.verticalScrollContentHeight) /
                        totalRowCount
                );
            } else {
                scrollY = deltaY;
            }
            if (scrollY) {
                const vs = this.verticalScroll.current;
                const oldScrollTop = vs.scrollTop;
                if (oldScrollTop) {
                    vs.scrollTop = oldScrollTop + scrollY;
                }
            }
        }
    }
    private updateSortedRowIndexes(props: TbodyProps<Row>) {
        if (!this.verticalScroll.current) {
            return;
        }
        const scroll = this.verticalScroll.current.scrollTop;
        if (!scroll) {
            return;
        }
        const totalRowCount = this.getTotalRowCountWithEmptyRow(props);
        const firstSortedRowIndex = Math.floor(
            (scroll * totalRowCount) / this.verticalScrollContentHeight
        );
        const lastSortedRowIndex = this.getLastSortedRowIndex(
            firstSortedRowIndex,
            props
        );
        if (
            this.state.firstSortedRowIndex !== firstSortedRowIndex ||
            this.state.lastSortedRowIndex !== lastSortedRowIndex
        ) {
            this.setState({ firstSortedRowIndex, lastSortedRowIndex });
        }
    }
    render() {
        const {
            columns,
            rows,
            sortedRowNumberToActualRowNumber,
            styles,
            getCellClassName,
            selection,
            onCellClick,
            onCellChanged,
            onCellPaste,
            onNewRowCellChanged,
            onHotkey,
            onBlur,
            onRowSelected,
            errors,
            errorMapping,
            tableId,
            rowRenderer,
            buildEmptyRow,
            withRowControlColumn,
            getRowKey,
        } = this.props;

        const evenRowClassNames: Array<string> = [];
        if (styles.rowClassName) {
            evenRowClassNames.push(styles.rowClassName);
        }
        evenRowClassNames.push(theme.trEven);
        const evenRowStyles: TrowStyles = {
            rowClassName: evenRowClassNames.join(" "),
            cellClassName: styles.cellClassName,
        };

        const firstIndex = this.state.firstSortedRowIndex;
        const lastIndex = Math.min(
            this.state.lastSortedRowIndex,
            this.props.rows.length - 1
        );
        const trows: Array<React.ReactNode> = [];
        const columnsWithGetRawData = columns.filter(
            (c) => c.getRawData !== undefined
        );
        const fullRowCellErrors: Array<ValidationError[][] | undefined> = [];
        const rowErrorGroups: Array<RowErrorGroup> = [];
        for (let index = 0; index <= rows.length; index++) {
            const row = rows[index];
            const rowNumber = sortedRowNumberToActualRowNumber
                ? sortedRowNumberToActualRowNumber[index]
                : index;
            const id = tableId + "[" + rowNumber + "]";
            const rowErrors = errors ? errors[id] : undefined;
            let atLeastOneCellError = false;
            const rowCellErrors =
                errors !== undefined
                    ? columns.map((col) => {
                          const cellId = id + "." + col.name;
                          const cellErrorId = col.getCellErrorId
                              ? col.getCellErrorId(row, rowNumber)
                              : cellId;
                          const cellErrors = errors[cellErrorId];
                          if (cellErrors !== undefined) {
                              atLeastOneCellError = true;
                          }
                          return cellErrors;
                      })
                    : undefined;
            fullRowCellErrors.push(
                atLeastOneCellError ? rowCellErrors : undefined
            );
            if (
                (rowErrors !== undefined && rowErrors.length > 0) ||
                atLeastOneCellError
            ) {
                rowErrorGroups.push({
                    rowNumber: index + 1,
                    rowErrors: rowErrors
                        ? rowErrors.map((rowError) =>
                              errorMapping![rowError.type]()
                          )
                        : [],
                    cellErrors:
                        rowCellErrors !== undefined
                            ? rowCellErrors
                                  .map((cellErrors, colIndex) => ({
                                      colLabel: columns[colIndex].label,
                                      errors: cellErrors
                                          ? cellErrors.map((cellError) =>
                                                errorMapping![cellError.type]()
                                            )
                                          : [],
                                  }))
                                  .filter(
                                      (cellErrors) =>
                                          cellErrors.errors.length > 0
                                  )
                            : [],
                });
            }
        }
        for (let index = firstIndex; index <= lastIndex; index++) {
            const row = rows[index];
            const rowNumber = sortedRowNumberToActualRowNumber
                ? sortedRowNumberToActualRowNumber[index]
                : index;
            const id = tableId + "[" + rowNumber + "]";
            const key = getRowKey ? getRowKey(row) : id;
            const rowErrors = errors ? errors[id] : undefined;
            const rawData = columnsWithGetRawData.map((col) =>
                col.getRawData!(row, rowNumber)
            );
            const sortedRowNumber = index;
            const isEvenRow = index % 2 === 1 ? true : false;
            const renderedRows = rowRenderer({
                key,
                id,
                row,
                columns,
                rowNumber,
                rowErrors,
                rowCellErrors: fullRowCellErrors[index],
                errorMapping,
                styles: isEvenRow ? evenRowStyles : styles,
                getCellClassName,
                selection,
                onCellClick,
                onCellChanged,
                onCellPaste,
                onHotkey,
                onBlur,
                buildEmptyRow,
                onRowSelected,
                rawData,
                withRowControlColumn,
                sortedRowNumber,
            });
            renderedRows.forEach((renderedRow) => trows.push(renderedRow));
        }
        if (
            onNewRowCellChanged &&
            buildEmptyRow &&
            this.state.lastSortedRowIndex === rows.length
        ) {
            const id = tableId + "[" + rows.length + "]";
            trows.push(
                <TEmptyRow
                    key={id}
                    id={id}
                    columns={columns}
                    rowNumber={rows.length}
                    withRowControlColumn={withRowControlColumn}
                    styles={styles}
                    getCellClassName={getCellClassName}
                    selection={selection}
                    onCellClick={onCellClick}
                    onNewRowCellChanged={onNewRowCellChanged}
                    onCellPaste={onCellPaste}
                    onHotkey={onHotkey}
                    onBlur={onBlur}
                    buildEmptyRow={buildEmptyRow}
                />
            );
        }
        // TODO rewrite this doc
        // The 'tbody' element contains everything (in particular, it has a scrollbar)
        // The 'wrapper' element is a wrapping div with a height set to the
        // total height of the rows if they were all rendered (as extrapolated
        // from the page that *is* rendered)
        // The 'viewPort' element contains the current page, it is moved along
        // depending on the page that is displayed
        //
        // Here is an illustration in ASCII art, with a legend. When there are
        // several different symbols at the same spot, this means elements are
        // over each other.
        //
        // ~~~~~~~~~~~~~
        // ~           ~
        // ~           ~            <=   ~   wrapper, as tall as all the rows
        // ~           ~                     if they were all displayed
        // +-+-+-+-+-+-+++
        // -  row 10   -^+          <=   -   viewport, as tall as the portion of the rows actually
        // ~  row 11   ~ +                   displayed - it is moved inside the wrapper depending
        // +  row 12   + +                   on which page is displayed
        // ~  row 13   ~ +
        // -  row 14   -v+          <=   +   tbody, as tall as the viewport, contains a scrollbar
        // +-+-+-+-+-+-+++                   since it contains an element bigger than itself
        // ~           ~                     (the wrapper) - the ^ and v show the scrollbar
        // ~           ~
        // ~           ~
        // ~~~~~~~~~~~~~
        //
        return (
            <div>
                <div
                    className={theme.tbody + " " + styles.bodyClassName}
                    ref={this.tbody}
                >
                    {this.buildStyles()}
                    <div
                        className={
                            this.state.scrollHorizontally
                                ? theme.tbodyWrapperScrollHorizontally
                                : theme.tbodyWrapper
                        }
                        ref={this.wrapper}
                        onScroll={(e) => this.handleHorizontalScroll(e)}
                        onWheel={(e) => this.handleWheel(e)}
                    >
                        <div className={theme.rowsContainer}>{trows}</div>
                    </div>
                    <div className={theme.tbodyVerticalScrollContainer}>
                        <div
                            className={theme.tbodyVerticalScroll}
                            ref={this.verticalScroll}
                            onScroll={(e) => this.handleVerticalScroll(e)}
                        >
                            <div className={theme.tbodyVerticalScrollContent} />
                        </div>
                    </div>
                </div>
                <TableErrorList rowErrorGroups={rowErrorGroups} />
            </div>
        );
    }
    private buildStyles() {
        // Build an empty style element, it will be filled by updateStylesContent()
        // whenever the table is updated or resized
        return <style ref={this.style} />;
    }
    private updateStylesContent(): void {
        if (!this.style.current) {
            return;
        }
        const { tableId } = this.props;
        const escapedId = CSS.escape(tableId);
        const styles: Array<string> = [];
        styles.push("@media screen {");
        if (this.theadLeft !== undefined) {
            styles.push(
                "[id='" +
                    escapedId +
                    "'] ." +
                    theme.thead +
                    " { left: " +
                    this.theadLeft +
                    "px; }"
            );
        }
        if (this.verticalScrollContentHeight !== undefined) {
            styles.push(
                "[id='" +
                    escapedId +
                    "'] ." +
                    theme.tbodyVerticalScrollContent +
                    " { height: " +
                    this.verticalScrollContentHeight +
                    "px; }"
            );
        }
        if (this.wrapperMinHeight) {
            styles.push(
                "[id='" +
                    escapedId +
                    "'] ." +
                    theme.tbodyWrapper +
                    " { min-height: " +
                    this.wrapperMinHeight +
                    "px; }"
            );
        }
        styles.push("}");
        this.style.current.innerHTML = styles.join("\n");
    }
    private getActiveSortedCellRowNumber(
        selection: RawSelectionDescriptor,
        props: TbodyProps<Row>
    ): number {
        const { actualRowNumberToSortedRowNumber, rows } = props;
        return selection.row.rawRowNumber !== rows.length &&
            actualRowNumberToSortedRowNumber
            ? actualRowNumberToSortedRowNumber[selection.row.rawRowNumber]
            : selection.row.rawRowNumber;
    }
    getViewWidth(): number {
        if (this.tbody.current) {
            return (this.tbody.current.parentNode as HTMLElement).clientWidth;
        } else {
            return 0;
        }
    }
}

type TbodyStyles = {
    bodyClassName: string;
} & TrowStyles;

interface TbodyProps<Row> {
    columns: Array<Column<Row, any, any, any>>;
    rows: Array<Row>;
    styles: TbodyStyles;
    getCellClassName?: (
        colName: string,
        rowNumber: number
    ) => string | undefined;
    tableId: string;
    verticalScrollHeight: number;
    getRowKey?: (row: Row) => string;
    errors?: ValidationErrors;
    errorMapping?: ErrorMapping;
    selection?: RawSelectionDescriptor;
    onCellClick?: (
        colName: string,
        rowNumber: number,
        isEditable: () => boolean,
        keepFocus: () => void
    ) => void;
    onCellChanged?: (colName: string, rowNumber: number, newData: any) => void;
    onNewRowCellChanged?: (
        colName: string,
        rowNumber: number,
        newData: any
    ) => void;
    onCellPaste?: (
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ) => void;
    onRowSelected?: (rowNumber: number) => void;
    buildEmptyRow?: (columns: Array<Column<Row, any, any, any>>) => Row;
    onHotkey: (e: CellKeyboardEvent, isEditable: () => boolean) => void;
    onBlur?: (colName: string, rowNumber: number) => void;
    rowRenderer: RowRenderer<Row>;
    maxVisibleRows?: number;
    sortedRowNumberToActualRowNumber: Array<number> | null;
    actualRowNumberToSortedRowNumber: Array<number> | null;
    withRowControlColumn?: boolean;
}

interface TbodyState {
    firstSortedRowIndex: number;
    lastSortedRowIndex: number;
    scrollHorizontally: boolean;
}

/**
 * A row in the table.
 *
 * Props:
 * - `columns` (array of objects, required): a list of columns to display. See the {@link Table} class for the expected format.
 * - `row` (object, required): a single row of data to render. Should follow the format of a row of the `rows` prop of @{link Table}.
 * - `id` (string, required): the identifier of the row.
 * - `rowNumber` (integer, required): the row number in the original data.
 * - `sortedRowNumber` (integer, required): the row number in the sorted data.
 * - `getCellClassName` (function, optional): a function returning the class of a cell. Should take a column name and a row number. Mostly useful for the selection handler.
 * - `selection` (RawSelectionDescriptor, optional): the selection descriptor (for editable tables).
 * - `onCellClick` (function, optional): function called when a cell is clicked. Should take the name of the column and the row number. Used by the selection handler.
 * - `onCellChanged` (function, optional): function called when the content of the cell has been changed (for editable tables). Only invoked if the data has actually changed. Should take the column name, the row number, the new data and the old row (with the old version of the data).
 * - `onCellPaste` (function, optional): function called when the user pasted data in a cell while the cell was the active cell and the mode was 'focus'. Should take the column name, the row number and the clipboard data (of type `DataTransfer`, see https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer).
 * - `onHotkey` (function, required): a function called when a hotkey has been pressed (keyup event, as we need arrow events, which only register on keyup) on a cell. Currently called for 'Enter' when a cell is in edit mode, and for arrow events when a cell is in focus mode. Should take the keyboard event.
 * - `onBlur` (function, mandatory if the table is editable): a function called when the cell was in focus mode and the user clicked elsewhere.
 * - `rowErrors` (array, optional): an array of strings, representing the error types for this row.
 * - `errors` (map of id => error, optional): maps an id (id of a row, of the shape "$id_of_table[$rowIndex]", or id of a cell, of the shape "$rowId$colName") to an error.
 * - `errorMapping` (map of error type => message, required if `errors` is
 * present) : maps an error type to a message.
 * - `rawData` (array, required): an array representing data outside the row, which impacts rendering (as returned by each column's `getRawData`). Should contain one entry by column with a `getRawData()` method, in the order of the columns of the array.
 * - `withRowControlColumn` (boolean, optional): if set to `true`, the column with row numbers will be displayed.
 * - `onRowSelected` (function, required if `withRowControlColumn` is true): function called when the user clicked on the column containing the row numbers. Should accept a row number.
 *
 * Note that due to the reliance on `shouldComponentUpdate`, only changes to
 * `row`, `rowErrors`, `errors` and the fact that a cell on this row was new
 * focused/unfocused or edited/finished edited will triggere a re-rendering.
 */
class Trow<Row> extends React.Component<TrowProps<Row>> {
    shouldComponentUpdate(nextProps: TrowProps<Row>) {
        // Some columns may have a getRawData - useful when they rely on data
        // in a closure, outside of the current row
        return (
            this.props.row !== nextProps.row ||
            this.props.columns !== nextProps.columns ||
            this.props.sortedRowNumber !== nextProps.sortedRowNumber ||
            this.props.rowErrors !== nextProps.rowErrors ||
            this.props.rowCellErrors !== nextProps.rowCellErrors ||
            this.cellErrorsChanged(nextProps) ||
            this.activeCellChangedOnThisRow(nextProps) ||
            this.selectedCellsChangedOnThisRow(nextProps) ||
            this.rawDataChanged(this.props.rawData, nextProps.rawData)
        );
    }
    private cellErrorsChanged(nextProps: TrowProps<Row>): boolean {
        const { id, columns, errors } = this.props;
        for (const col of columns) {
            const cellId = id + "." + col.name;
            const oldCellError = errors ? errors[cellId] : null;
            const newCellError = nextProps.errors
                ? nextProps.errors[cellId]
                : null;
            if (oldCellError !== newCellError) {
                return true;
            }
        }
        return false;
    }
    private activeCellChangedOnThisRow(nextProps: TrowProps<Row>): boolean {
        const wasCurrentCellOnRow =
            !!this.props.selection &&
            this.props.rowNumber === this.props.selection.row.rawRowNumber;
        const isCurrentCellNowOnRow =
            !!nextProps.selection &&
            nextProps.rowNumber === nextProps.selection.row.rawRowNumber;
        const isCurrentCellOnSameRowButNewColumn =
            wasCurrentCellOnRow &&
            isCurrentCellNowOnRow &&
            this.props.selection.columnName !== nextProps.selection!.columnName;
        const isCurrentCellAtSameSpotButNewMode =
            wasCurrentCellOnRow &&
            isCurrentCellNowOnRow &&
            this.props.selection.columnName ===
                nextProps.selection!.columnName &&
            this.props.selection.type !== nextProps.selection!.type;
        const activeCellChanged =
            (wasCurrentCellOnRow && !isCurrentCellNowOnRow) ||
            (!wasCurrentCellOnRow && isCurrentCellNowOnRow) ||
            isCurrentCellOnSameRowButNewColumn ||
            isCurrentCellAtSameSpotButNewMode;
        return activeCellChanged;
    }
    private selectedCellsChangedOnThisRow(nextProps: TrowProps<Row>): boolean {
        if (!nextProps.selection && !this.props.selection) {
            return false;
        }
        const oldSelection = this.props.selection;
        const newSelection = nextProps.selection;
        let wasRowSelected = false;
        if (oldSelection) {
            const [oldSelectionStartRowNumber, oldSelectionEndRowNumber] =
                this.getSelectionRowRange(oldSelection);
            wasRowSelected =
                oldSelectionStartRowNumber <= this.props.rowNumber &&
                this.props.rowNumber <= oldSelectionEndRowNumber;
        }
        let isRowSelected = false;
        if (newSelection) {
            const [newSelectionStartRowNumber, newSelectionEndRowNumber] =
                this.getSelectionRowRange(newSelection);
            isRowSelected =
                newSelectionStartRowNumber <= nextProps.rowNumber &&
                nextProps.rowNumber <= newSelectionEndRowNumber;
        }
        if (wasRowSelected !== isRowSelected) {
            return true;
        }
        if (!isRowSelected) {
            return false;
        }
        const oldAnchorColumnName =
            oldSelection && oldSelection.type === "SELECT"
                ? oldSelection.anchorColumnName
                : undefined;
        const newAnchorColumnName =
            newSelection && newSelection.type === "SELECT"
                ? newSelection.anchorColumnName
                : undefined;
        return (
            oldSelection!.columnName !== newSelection!.columnName ||
            oldAnchorColumnName !== newAnchorColumnName
        );
    }
    private rawDataChanged(
        oldRawData: Array<any>,
        newRawData: Array<any>
    ): boolean {
        let rawDataChanged = oldRawData.length !== newRawData.length;
        if (!rawDataChanged) {
            for (let i = 0; i < oldRawData.length; i++) {
                const oldRawCell = oldRawData[i];
                const newRawCell = newRawData[i];
                if (oldRawCell !== newRawCell) {
                    rawDataChanged = true;
                    break;
                }
            }
        }
        return rawDataChanged;
    }
    private getSelectionRowRange(
        selection: RawSelectionDescriptor
    ): [number, number] {
        const rowNumbers = [selection.row.rawRowNumber];
        if (selection.type === "SELECT") {
            rowNumbers.push(selection.anchorRow.rawRowNumber);
        } else {
            rowNumbers.push(rowNumbers[0]);
        }
        rowNumbers.sort((a, b) => {
            if (a < b) {
                return -1;
            } else if (a > b) {
                return 1;
            } else {
                return 0;
            }
        });
        return [rowNumbers[0], rowNumbers[1]];
    }
    render() {
        const {
            row,
            columns,
            id,
            rowNumber,
            styles,
            getCellClassName,
            selection,
            onCellChanged,
            onCellPaste,
            onHotkey,
            onBlur,
            rowErrors,
            rowCellErrors,
            errorMapping,
            withRowControlColumn,
        } = this.props;
        const maybeRowControlColumn = withRowControlColumn
            ? this.buildRowControlColumn()
            : null;
        const actualStyles: TrowStyles = clone(styles);
        if (rowErrors) {
            actualStyles.rowClassName =
                actualStyles.rowClassName + " " + theme.errorElement;
        }
        const tooltip = rowErrors
            ? rowErrors
                  .map((error) => errorMapping![error.type]() || error.type)
                  .join("\n")
            : undefined;
        const handleClicks = this.handleClickResolver(this.props);
        return (
            <Tr styles={actualStyles}>
                {maybeRowControlColumn}
                {columns.map((col, index) => {
                    const data = col.getData(row, rowNumber);
                    const editData = col.getRawData
                        ? col.getRawData(row, rowNumber)
                        : data;
                    const textData = col.getTextData
                        ? col.getTextData(row, rowNumber)
                        : undefined;
                    const customCellClassName = getCellClassName
                        ? getCellClassName(col.name, rowNumber)
                        : null;
                    const cellClassNames: Array<string> = [];
                    if (actualStyles.cellClassName) {
                        cellClassNames.push(actualStyles.cellClassName);
                    }
                    if (customCellClassName) {
                        cellClassNames.push(customCellClassName);
                    }
                    if (col.cellClassName) {
                        cellClassNames.push(col.cellClassName);
                    }
                    const cellClassName = cellClassNames.join(" ");
                    const isCellCurrentCell =
                        !!selection &&
                        col.name === selection.columnName &&
                        rowNumber === selection.row.rawRowNumber;
                    const isFocused =
                        isCellCurrentCell &&
                        (selection.type === "FOCUS" ||
                            selection.type === "SELECT");
                    const isEdited =
                        isCellCurrentCell && selection.type === "EDIT";
                    const cellId = id + "." + col.name;
                    const cellErrors = rowCellErrors
                        ? rowCellErrors[index]
                        : undefined;
                    return (
                        <Tcell
                            key={cellId}
                            id={cellId}
                            data={data}
                            editData={editData}
                            textData={textData}
                            column={col}
                            rowNumber={rowNumber}
                            cellClassName={cellClassName}
                            onClick={handleClicks[index]}
                            onChange={onCellChanged}
                            onPaste={onCellPaste}
                            isFocused={isFocused}
                            isEdited={isEdited}
                            onHotkey={onHotkey}
                            onBlur={onBlur}
                            rowTooltip={tooltip}
                            cellErrors={cellErrors}
                            errorMapping={errorMapping}
                        />
                    );
                })}
            </Tr>
        );
    }
    private handleClickResolver = createSelector(
        (props: TrowProps<Row>) => props.columns,
        (props: TrowProps<Row>) => props.onCellClick,
        (columns, onCellClick) => {
            return columns.map((col) => {
                if (col.onClick || onCellClick) {
                    return (
                        colName: string,
                        rowNumber: number,
                        isEditable: () => boolean,
                        keepFocus: () => void
                    ) => {
                        if (col.onClick) {
                            col.onClick(this.props.row, rowNumber);
                        }
                        if (onCellClick) {
                            onCellClick(
                                colName,
                                rowNumber,
                                isEditable,
                                keepFocus
                            );
                        }
                    };
                } else {
                    return undefined;
                }
            });
        }
    );
    private buildRowControlColumn(): React.ReactNode {
        // Build the 'td' containing the row number
        const { rowNumber, sortedRowNumber, onRowSelected } = this.props;

        return (
            <div
                className={theme.td + " " + theme.rowControlColumn}
                onClick={
                    onRowSelected ? () => onRowSelected(rowNumber) : undefined
                }
            >
                {sortedRowNumber + 1}
            </div>
        );
    }
}

type TrowStyles = TrStyles & TdStyles;

interface TrowProps<Row> {
    columns: Array<Column<Row, any, any, any>>;
    row: Row;
    id: string;
    rowNumber: number;
    styles: TrowStyles;
    getCellClassName?: (
        colName: string,
        rowNumber: number
    ) => string | undefined;
    selection?: RawSelectionDescriptor;
    onCellClick?: (
        colName: string,
        rowNumber: number,
        isEditable: () => boolean,
        keepFocus: () => void
    ) => void;
    onCellChanged?: (colName: string, rowNumber: number, newData: any) => void;
    onCellPaste?: (
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ) => void;
    buildEmptyRow?: (columns: Array<Column<Row, any, any, any>>) => Row;
    onBlur?: (colName: string, rowNumber: number) => void;
    onHotkey: (e: CellKeyboardEvent, isEditable: () => boolean) => void;
    rowErrors?: Array<ValidationError>;
    rowCellErrors?: Array<Array<ValidationError>>;
    errors?: ValidationErrors;
    errorMapping?: ErrorMapping;
    rawData: Array<any>;
    withRowControlColumn?: boolean;
    onRowSelected?: (rowNumber: number) => void;
    sortedRowNumber: number;
}

function addLinebreaks(data: any, keyPrefix: string): React.ReactNode {
    let formattedData: React.ReactNode;
    if (data === null || typeof data !== "string") {
        formattedData = data;
    } else {
        const splitData = data.split("\n");
        if (splitData.length === 1) {
            formattedData = data; // No need to create React nodes in the majority of cases
        } else {
            formattedData = splitData.map(
                (item: React.ReactNode, i: number) => (
                    <span key={keyPrefix + "_span" + i}>
                        {item}
                        <br />
                    </span>
                )
            );
        }
    }
    return formattedData;
}

/**
 * A table cell. Renders as a {@link Td} tag.
 *
 * Props:
 * - `column` (object, required): the column of the data to display. Should follow the format of an element of the `columns` prop of the {@link Table} tag.
 * - `data` (string or React node, optional}: the data to render.
 * - `row` (object, required): the current row.
 * - `rowNumber` (integer, required): the row number of the current row.
 * - `styles` (TcellStyles): the set of the CSS classes to use.
 * - `onClick` (function, optional): function called when a cell is clicked. Should take the name of the column and the row number. Used by the selection handler.
 * - `onChange` (function, optional): function called when the content of the cell has been changed (for editable tables). Only invoked if the data has actually changed. Should take the column name, the row number, the new data and the old row (with the old version of the data).
 * - `onPaste` (function, optional): function called when the user pasted data in a cell while the cell was the active cell and the mode was 'focus'. Should take the column name, the row number and the clipboard data (of type `DataTransfer`, see https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer).
 * - `onBlur` (function, mandatory if the table is editable): a function called when the cell was in focus mode and the user clicked elsewhere.
 * - `isFocused` (boolean, required): true if the cell is currently selected and the selection mode is 'focus'.
 * - `isEdited` (boolean, required): true if the cell is currently selected and the selection mode is 'edit'.
 * - `id` (string, required): the id of the cell.
 * - `cellErrors` (array, optional): an array of strings, representing the error types for this cell.
 * - `errorMapping` (map of error type => message, required if `errors` is present) : maps an error type to a message.
 * - `rowTooltip` (string, optional): the tooltip for the current row.
 *
 * State:
 * - `initialEditValue`: when switching to edit mode, the value that will be passed to the editor. Normally equal to `data`, except when the user switches to edit mode by typing directly in the cell. In this case, whatever letter the user typed is used.
 */
class Tcell<Data, Row> extends React.PureComponent<
    TcellProps<Data, Row>,
    TcellState<Data>
> {
    private blurable: Blurable | null;
    private pauseKeyTrap: boolean;
    private pasteInput: any;
    private dontBlurPasteInput: boolean;
    constructor(props: TcellProps<Data, Row>) {
        super(props);
        this.blurable = null;
        this.pauseKeyTrap = false;
        this.dontBlurPasteInput = false;
        this.state = {
            initialEditValue: props.editData,
        };
    }
    componentWillMount() {
        this.setState({ initialEditValue: this.props.editData });
    }
    componentWillReceiveProps(nextProps: TcellProps<Data, Row>) {
        if (
            this.state.initialEditValue === null ||
            this.props.editData !== nextProps.editData ||
            !nextProps.isEdited
        ) {
            this.setState({ initialEditValue: nextProps.editData });
        }
    }
    render() {
        const {
            column,
            data,
            editData,
            textData,
            rowNumber,
            cellClassName,
            onClick,
            onChange,
            isFocused,
            isEdited,
            id,
            cellErrors,
            errorMapping,
            rowTooltip,
        } = this.props;
        let cellContent: React.ReactNode;
        let EditorElt: Editor<Data> | null = null;
        if (isEdited && column.getEditor) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            EditorElt = column.getEditor(rowNumber, editData);
        }
        this.blurable = null;
        this.pasteInput = null;
        if (EditorElt) {
            cellContent = (
                <EditorElt
                    data={this.state.initialEditValue}
                    maxLength={column.maxEditorLength}
                    onBlurableRef={(blurable: Blurable) =>
                        (this.blurable = blurable)
                    }
                    onBlur={(newData: Data | null) => {
                        if (onChange && newData !== editData) {
                            onChange(column.name, rowNumber, newData);
                        }
                    }}
                />
            );
        } else {
            let readonlyContent: React.ReactNode;
            if (data === null || data === undefined || data === "") {
                // avoid zero-height rows
                readonlyContent = "\xa0";
            } else {
                readonlyContent = addLinebreaks(data, id);
            }
            // Insert a zero-width input element. This is the only way to
            // handle pasting in 'focus' mode in IE and Firefox, which require
            // a focused, visible input element for the paste event.
            cellContent = isFocused ? (
                <span>
                    <input
                        ref={(input) => {
                            this.pasteInput = input;
                            if (input) {
                                input.focus();
                            }
                        }}
                        className={theme.pasteInput}
                        onPaste={(e) => this.handlePaste(e)}
                        onBlur={(_) => {
                            this.handlePasteInputBlur();
                        }}
                        maxLength={0}
                    />
                    {readonlyContent}
                </span>
            ) : (
                readonlyContent
            );
        }
        const classNames = ["col-" + SpecCharsCSSEscape(column.name)];
        if (cellClassName) {
            classNames.push(cellClassName);
        }
        if (cellErrors) {
            classNames.push(theme.errorElement);
        }
        const actualClassName = classNames.join(" ");
        const tooltips: Array<string> = [];
        if (textData !== null && textData !== undefined) {
            tooltips.push(textData);
        } else if (
            data !== null &&
            (typeof data === "string" || typeof data === "number")
        ) {
            tooltips.push(data.toString());
        }
        if (rowTooltip) {
            tooltips.push(rowTooltip);
        }
        if (cellErrors) {
            const cellTooltip = cellErrors
                .map((error) => errorMapping![error.type]())
                .join("\n");
            tooltips.push(cellTooltip);
        }
        const tooltip = tooltips.join("\n");
        return (
            <Td
                cellClassName={actualClassName}
                onClick={onClick ? this.handleClick : undefined}
                isFocused={isFocused}
                title={tooltip}
            >
                {isFocused || isEdited ? (
                    <KeyTrap
                        onLocalKeyUp={this.handleKeyUp()}
                        onLocalKeyDown={this.handleKeyDown()}
                        unstable_localKeyHandle={(e) => {
                            const { isEdited, onHotkey } = this.props;
                            if (
                                !isEdited &&
                                !e.ctrlKey &&
                                e.charCode > 0x001f &&
                                e.charCode !== 0x007f &&
                                (e.charCode < 0x0080 || e.charCode > 0x009f)
                            ) {
                                const cellKeyboardEvent = {
                                    type: CellKeyboardEventType.START_EDITION,
                                };
                                // In chrome, the underlying textarea gets the next keyup event twice
                                // and it leads to doubling the typed letter. The setTimeout breaks
                                // the dreadful chained reaction.
                                setTimeout(() => {
                                    this.setState({
                                        initialEditValue: String.fromCharCode(
                                            e.charCode
                                        ),
                                    });
                                    onHotkey(cellKeyboardEvent, () =>
                                        this.isCellEditable()
                                    );
                                }, 1);
                                return false;
                            }
                            return true;
                        }}
                    >
                        {cellContent}
                    </KeyTrap>
                ) : (
                    cellContent
                )}
            </Td>
        );
    }
    private handleClick = (): void => {
        const { column, rowNumber, onClick } = this.props;
        if (onClick) {
            onClick(
                column.name,
                rowNumber,
                () => this.isCellEditable(),
                () => {
                    if (this.pasteInput) {
                        // Prevent erroneous blur of the paste input.
                        this.dontBlurPasteInput = true;
                        // And give the focus back of course.
                        this.pasteInput.focus();
                    }
                }
            );
        }
    };
    private handlePaste(e: React.ClipboardEvent<HTMLElement>): void {
        e.preventDefault();
        if (this.props.onPaste) {
            this.props.onPaste(
                this.props.column.name,
                this.props.rowNumber,
                e.clipboardData
            );
        }
    }
    handleKeyDown() {
        const handleArrow = (e: KeyboardEvent) => {
            if (this.pauseKeyTrap) {
                // Propagate event not to loose keyed text. See handlePasteInputBlur.
                return true;
            }
            let stopEvent = false;
            const { onHotkey, isFocused } = this.props;
            if (isFocused) {
                // Never forward arrow events when in edit mode, otherwise we can't move around
                const type = e.shiftKey
                    ? CellKeyboardEventType.SELECTION_MOVEMENT
                    : CellKeyboardEventType.MOVEMENT;
                stopEvent = true;
                const cellKeyboardEvent = {
                    type,
                    key: e.key,
                };
                onHotkey(cellKeyboardEvent, () => this.isCellEditable());
            }
            return !stopEvent;
        };
        const handleDelete = () => {
            if (this.pauseKeyTrap) {
                // Propagate event not to loose keyed text. See handlePasteInputBlur.
                return true;
            }
            const { onHotkey, isEdited } = this.props;
            let stopEvent = false;
            if (!isEdited) {
                stopEvent = true;
                const cellKeyboardEvent = {
                    type: CellKeyboardEventType.DELETION,
                };
                onHotkey(cellKeyboardEvent, () => this.isCellEditable());
            }
            return !stopEvent;
        };
        const handleTab = (e: KeyboardEvent) => {
            if (this.pauseKeyTrap) {
                // Propagate event not to loose keyed text. See handlePasteInputBlur.
                return true;
            }
            const { onHotkey, isEdited, isFocused } = this.props;
            let stopEvent = false;
            let cellKeyboardEvent: CellKeyboardEvent | null = null;
            if (!isEdited && isFocused) {
                stopEvent = true;
                cellKeyboardEvent = {
                    type: e.shiftKey
                        ? CellKeyboardEventType.PREVIOUS_CELL
                        : CellKeyboardEventType.NEXT_CELL,
                };
            } else if (isEdited) {
                stopEvent = true;
                if (this.blurable) {
                    this.blurable.blur();
                }
                cellKeyboardEvent = {
                    type: e.shiftKey
                        ? CellKeyboardEventType.END_EDITION_AND_PREVIOUS_CELL
                        : CellKeyboardEventType.END_EDITION_AND_NEXT_CELL,
                };
            }
            if (cellKeyboardEvent) {
                onHotkey(cellKeyboardEvent, () => this.isCellEditable());
            }
            return !stopEvent;
        };
        return {
            up: handleArrow,
            "shift+up": handleArrow,
            down: handleArrow,
            "shift+down": handleArrow,
            left: handleArrow,
            "shift+left": handleArrow,
            right: handleArrow,
            "shift+right": handleArrow,
            tab: handleTab,
            "shift+tab": handleTab,
            backspace: handleDelete,
            del: handleDelete,
        };
    }
    private handleKeyUp() {
        return {
            "ctrl+a": () => {
                if (this.pauseKeyTrap) {
                    return true;
                }
                const { onHotkey } = this.props;
                onHotkey({ type: CellKeyboardEventType.SELECT_ALL }, () =>
                    this.isCellEditable()
                );
                return false;
            },
            "ctrl+c": () => {
                if (this.pauseKeyTrap) {
                    return true;
                }
                const { onHotkey } = this.props;
                onHotkey({ type: CellKeyboardEventType.COPY }, () =>
                    this.isCellEditable()
                );
                return false;
            },
            esc: () => {
                if (this.pauseKeyTrap) {
                    return true;
                }
                const { isEdited, onHotkey } = this.props;
                if (isEdited) {
                    onHotkey({ type: CellKeyboardEventType.END_EDITION }, () =>
                        this.isCellEditable()
                    );
                }
                return false;
            },
            enter: () => {
                if (this.pauseKeyTrap) {
                    // Propagate event not to lose keyed text. See handlePasteInputBlur.
                    return true;
                }
                const { onHotkey, isEdited } = this.props;
                if (this.blurable) {
                    this.blurable.blur();
                }
                const type = isEdited
                    ? CellKeyboardEventType.END_EDITION
                    : CellKeyboardEventType.START_EDITION;
                onHotkey({ type }, () => this.isCellEditable());
                return false;
            },
        };
    }
    private isCellEditable(): boolean {
        const { column } = this.props;
        if (column) {
            if (column.isEditable) {
                const { rowNumber } = this.props;
                return column.isEditable(rowNumber);
            } else if (column.getEditor) {
                // Don't call getEditor.
                // If getEditor may return null then implement isEditable
                // to refine the focus behaviour.
                return true;
            }
        }
        return false;
    }
    private handlePasteInputBlur(): void {
        // This callback is invoked when the focus on the dummy zero-width input is lost
        // We use a setTimeout call with a long delay (1s). The issue is that,
        // when we get the blur event, we don't know what the new focus target
        // is. The input may be losing focus because we switched the cell to
        // edit mode (in which case the onBlur() prop should not be invoked).
        // We wait one second before checking if our isEdited prop is set to
        // true, in which case we send our column name and our row number to
        // the selection handler.  If the new focus target was another cell,
        // the selection handler will have been notified in the meantime, and
        // won't do anything. However, if the selection handler's currently
        // selected DID NOT change in the meantime, this means the new focus
        // target wasnot the current cell or another cell, but an element
        // outside. In this case, the selection handler will consider that no
        // cell is selected anymore. This is critical for tracking keyboard
        // events correctly, since they are global and are not tied to a
        // particular DOM element - therefore, we absolutely need to know if a
        // cell of the current table is selected or not (if it isn't, keyboard
        // events need to be ignored).
        const { onBlur, column, rowNumber } = this.props;
        // Nevertheless, pause the global key trap so prevent from grabbing text
        // that is not intended to the editor.
        this.pauseKeyTrap = true;
        setTimeout(() => {
            // Very important: remove key trap pause
            this.pauseKeyTrap = false;
            if (!this.dontBlurPasteInput && !this.props.isEdited) {
                if (onBlur) {
                    onBlur(column.name, rowNumber);
                }
            }
            this.dontBlurPasteInput = false;
        }, 1000);
    }
}

interface TcellProps<Data, Row> {
    id: string;
    column: Column<Row, Data, any, any>;
    data: any;
    editData: any;
    rowNumber: number;
    cellClassName: string;
    onClick?: (
        colName: string,
        rowNumber: number,
        isEditable: () => boolean,
        keepFocus: () => void
    ) => void;
    onChange?: (colName: string, rowNumber: number, newData: any) => void;
    onPaste?: (
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ) => void;
    onBlur?: (colName: string, rowNumber: number) => void;
    onHotkey: (e: CellKeyboardEvent, isEditable: () => boolean) => void;
    isFocused: boolean;
    isEdited: boolean;
    cellErrors?: Array<ValidationError>;
    errorMapping?: ErrorMapping;
    rowTooltip?: string;
    textData?: string;
}

interface TcellState<RawData> {
    initialEditValue: RawData | string;
}

/**
 * An empty row (as displayed at the bottom of an editable table).
 *
 * Takes a subset of the props available to {@link Trow}:
 * - `id`
 * - `columns`
 * - `rowNumber`
 * - `getCellClassName`
 * - `selection`
 * - `onCellClick`
 * - `onCellPaste`
 * - `onHotkey`
 * - `onBlur`
 * - `buildEmptyRow`
 *
 * It has the following specific props:
 * - `onNewRowCellChanged`: similar to `Trow`'s `onCellChanged`.
 */
class TEmptyRow<Row> extends React.Component<TEmptyRowProps<Row>> {
    render() {
        const {
            columns,
            id,
            rowNumber,
            styles,
            getCellClassName,
            selection,
            onCellClick,
            onNewRowCellChanged,
            onCellPaste,
            withRowControlColumn,
            onHotkey,
            onBlur,
            buildEmptyRow,
        } = this.props;
        const row = buildEmptyRow(columns);
        const maybeEmptyRowControlColumn = withRowControlColumn
            ? this.buildEmptyRowControlColumn(styles)
            : null;
        return (
            <Tr styles={styles}>
                {maybeEmptyRowControlColumn}
                {columns.map((col) => {
                    const data = col.getData(row, rowNumber);
                    const editData = col.getRawData
                        ? col.getRawData(row, rowNumber)
                        : data;
                    const customCellClassName = getCellClassName
                        ? getCellClassName(col.name, rowNumber)
                        : null;
                    const cellClassNames: Array<string> = [];
                    if (styles.cellClassName) {
                        cellClassNames.push(styles.cellClassName);
                    }
                    if (customCellClassName) {
                        cellClassNames.push(
                            SpecCharsCSSEscape(customCellClassName)
                        );
                    }
                    if (col.cellClassName) {
                        cellClassNames.push(
                            SpecCharsCSSEscape(col.cellClassName)
                        );
                    }
                    const cellClassName = cellClassNames.join(" ");
                    const isCellCurrentCell =
                        !!selection &&
                        col.name === selection.columnName &&
                        rowNumber === selection.row.rawRowNumber;
                    const isFocused =
                        isCellCurrentCell &&
                        (selection.type === "FOCUS" ||
                            selection.type === "SELECT");
                    const isEdited =
                        isCellCurrentCell && selection.type === "EDIT";
                    const cellId = id + col.name;
                    return (
                        <Tcell
                            key={cellId}
                            id={cellId}
                            data={data}
                            editData={editData}
                            textData={undefined}
                            column={col}
                            rowNumber={rowNumber}
                            cellClassName={cellClassName}
                            onClick={onCellClick}
                            onChange={onNewRowCellChanged}
                            isFocused={isFocused}
                            isEdited={isEdited}
                            onPaste={onCellPaste}
                            onHotkey={onHotkey}
                            onBlur={onBlur}
                        />
                    );
                })}
            </Tr>
        );
    }
    private buildEmptyRowControlColumn(styles: TdStyles): React.ReactNode {
        return (
            <Td
                cellClassName={
                    styles.cellClassName + " " + theme.rowControlColumn
                }
            />
        );
    }
}

interface TEmptyRowProps<Row> {
    id?: string;
    columns: Array<Column<Row, any, any, any>>;
    rowNumber: number;
    withRowControlColumn?: boolean;
    styles: TrowStyles;
    getCellClassName?: (
        colName: string,
        rowNumber: number
    ) => string | undefined;
    selection?: RawSelectionDescriptor;
    onCellClick?: (
        colName: string,
        rowNumber: number,
        isEditable: () => boolean,
        keepFocus: () => void
    ) => void;
    onNewRowCellChanged?: (
        colName: string,
        rowNumber: number,
        newData: any
    ) => void;
    onCellPaste?: (
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ) => void;
    buildEmptyRow: (columns: Array<Column<Row, any, any, any>>) => Row;
    onBlur?: (colName: string, rowNumber: number) => void;
    onHotkey: (e: CellKeyboardEvent, isEditable: () => boolean) => void;
}

interface ColNameToWidth {
    [colName: string]: number;
}

/**
 * functions exposed by the table component.
 */
interface TableLike {
    /**
     * Will copy every data to the clipboard.
     */
    copyAllToClipboard(): void;
    /**
     * Will copy a specific selection of data to the clipboard.
     */
    copyAreaToClipboard(
        colNames: Array<string>,
        rowNumbers: Array<number>
    ): void;
    /**
     * Will copy the current selection to the clipboard.
     */
    copySelectionToClipboard(): void;
}

function emptyFn() {}

/**
 * A sortable table. Renders as a `div` with a class of `reactable`. May be sortable. May also be edited.
 *
 * This is a large, complex but quite flexible implementation. When editable, supports 'focus' mode (display a border around the current cell, the focused cell can be changed via clicking or arrows), and 'edit' mode, in which an editor is displayed in the cell and the content can be changed. It also supports pasting in focus mode (where, similarly to Excel, it is possible to paste multiple cells). In addition, the 'select' mode makes possible to shift-select multiple cells.
 *
 * Props:
 * - `id` (string, optional): a unique identifier. Mostly useful for error handling, defaults to "table".
 * - `columns` (array of objects, required): the list of columns to display. Each item of the `columns` prop should follow the following format:
 *   - `name` (string, required): the identifier of the column. Used whenever there is a need to refer to the column.
 *   - `label` (string, required): the label to display in the header
 *   - `getData` (function, required): a function to call in order to render the data. Should accept the row and its row number as parameters and return a string or a React node. If missing, the data value is directly rendered.
 *   - `getTextData` (function, optional): a function to call in order to render a text version of the data. Should accept the row and its row number as parameters and return a string. This is used for creating tooltips. If missing, the tooltip will contain the value returned by `getData()` if this value is a string or a number.
 *   - `getSortData` (function, optional): a function to use when comparing two values for sorting. Should accept the row as parameter and return something that can be compared. If missing, the data value is used for comparison.
 *   - `getRawData` (function, optional): a function to use when trying to decide if a row has been updated. Per default, only a difference (via `===`) between the current row and the row in the new props is taken into account. Providing `getRawData` makes it possible to refresh rows where some of the cell rely on data in a closure, outside of the current row. If that is the case, make sure you do not close by accident over an immutable data structure (eg, do row => this.props.yourData, not row => yourData). `getRawData` is also used to provide the editor with an initialValue, if not found then the result of `getData` is used.
 *   - `cellClassName` (string, optional): the name of the CSS class to apply on each `<Td>` of the column.
 *   - `headerClassName` (string, optional): the name of the CSS class to apply on the `<Th>` of the column.
 *   - `editor` (React element class, optional): the React element to render when the cell switches to 'edit' mode. Should expose a blur() function (which should trigger the invoking of the onBlur() callback) and accept the following props:
 *       - `data` (object): the cell's data
 *       - `row` (object): the current row
 *       - `onBlur` (function): a function to invoke the editor loses focus. Should receive the new value of the data as parameter.
 *    - `fixedWidth` (number, optional): the (fixed) width of the column, in pixels. A column with `fixedWidth` may not have `minWidth` set.
 *    - `minWidth` (number, optional): the minimum width of the column, in pixels. A column with `minWidth` may not have `fixedWidth` set.
 *    - `headerWidget` (React element, optional): if set, is displayed instead of the label.
 * - `rows` (array of objects, required): a list of rows to render. As they are a list of objects, they are displayed in the order of the `columns` prop. The value of each cell is looked up using the `name` property of the column.
 * - `sortColumn` (string, optional): The name of the column to sort by.
 * - `orderBy` (string, optional): The direction of the ordering (should be one of `"ASC"` or `"DESC"`).
 * - `sortType` (string, optional): The type of sorting. If different from `"manual"` or `"external"`, the table is not sortable.
 * - `defaultSortColumn` (string, optional): The name of the column `rows` is sorted by, if any. Used to avoid sorting again if it's unnecessary.
 * - `defaultOrderBy` (string, optional): The direction `rows` is sorted by, if any (should be one of `"ASC"` or `"DESC"`). Defaults to `"ASC"` if `defaultSortColumn` is set.
 * - `onSortChanged` (function, optional): a callback invoked when the ordering has been modified. Should accept the name of the new sort column and the new sort order (one of `"ASC"`, `"DESC"`).
 * - `className` (string, optional): the class name of the table. Defaults to "table-default sortable-table".
 * - `editable` (boolean, optional): if set to `true`, the table may be navigated by selecting a cell and using the arrow keys (think Excel).
 * - `activeCellClassName` (string, optional): if `editable` is `true`, the name of the class for the active cell (defaults to `"active-cell"`)
 * - `editedCellClassName` (string, optional): if `editable` is `true`, the name of the class for the currently edited cell (defaults to `"edited-cell"`)
 * - `selectedCellClassName` (string, optional): if `editable` is `true`, the name of the class a cell which is part of a multi-cell selection (defaults to `"selected-cell"`)
 * - `topSelectionClassName` (string, optional): if `editable` is `true`, the name of the class a cell which is part of the first row of a multi-cell selection (defaults to `"top-selection"`)
 * - `bottomSelectionClassName` (string, optional): if `editable` is `true`, the name of the class a cell which is part of the last row of a multi-cell selection (defaults to `"bottom-selection"`)
 * - `leftSelectionClassName` (string, optional): if `editable` is `true`, the name of the class a cell which is part of the leftmost column of a multi-cell selection (defaults to `"left-selection"`)
 * - `rightSelectionClassName` (string, optional): if `editable` is `true`, the name of the class a cell which is part of the rightmost column of a multi-cell selection (defaults to `"right-selection"`)
 * - `errors` (map of id => error, optional): maps an id (id of a row, of the shape "$id_of_table[$rowIndex]", or id of a cell, of the shape "$rowId$colName") to an error.
 * - `errorMapping` (map of error type => message, required if `errors` is
 * present) : maps an error type to a message.
 * - `showFirstError` (boolean, optional): if true, the table will scroll to the row containing the first error found in `errors`, and then invoke `onFirstErrorShown`. Only useful if `maxVisibleRows` is set, otherwise just use the `ErrorGroup` component.
 * - `onFirstErrorShown` (function, mandatory if `showFirstError` is true): a function invoked once the table has scrolled to the location of the first error (if any). Should reset the parent component/store "show first error" flag. This function shouldn't expect arguments.
 * - `rowRenderer` (function, optional): takes named parameters with the same signature as `Trow`'s props. Returns a (possibly empty) list of React elements to render. Defaults to defaultRowRenderer.
 * - `onDeleteCells` (function, optional): if `editable` is set to `true`, will be invoked when the user selects a block and hits "Delete" or "Backspace". Should accept two parameters: a list of column names, and a list of row numbers.
 * - `onActiveCellChanged` (function, optional): if `editable` is set to `true`, will be invoked whenever the current active cell changes. Should accept a column name and a row number. If the cell has been blurred, the parameters will be (null, -1).
 * - `buildEmptyRow` (function, mandatory if `onNewRowCellChanged` is present): a function which returns an empty row (this is necessary as the table relies on the `getData()` function of each column and has no knowledge of the structure of individual rows).
 * - `getCellClassName` (function, optional): returns the class name of the given cell. Should take the column name and row number as parameter.
 * - `maxVisibleRows` (number, optional): if present, only up to `maxVisibleRows` rows are displayed at a time, and a scrollbar lets the user move up and down.
 * - `getCopyable` (function, optional): if present, used when the user copies something in focus or select mode. Should expect a list of column names and a list of row numbers. Should return a string or `null` if the data cannot be copied.
 * - `getRowKey` (function, optional): if present, is used to generate the React key of the row.
 *
 * State:
 * - `sortedRows` (array, defaults to []): a sorted version of the `rows` props (if sortType=manual) or the same value as `rows`.
 * - `sortedRowNumberToActualRowNumber` (array, defaults to null): a list of before-sorting row numbers (from the `rows` props), in the current sorting order (eg, if rows [ ["a"], ["b"], ["c"] ] are currently sorted as [ ["b"], ["a"], ["c"] ], would be equal to [1, 0, 2]). Is null if rows are not sorted.
 * - `actualRowNumberToSortedRowNumber` (array, defaults to null): the invert of `sortedRowNumberToActualRowNumber`
 * - `sortColumn` (string, defaults to null): the name of the current sort column.
 * - `orderBy` (string, defaults to null): the direction of the current sort (should be one of `"ASC"`, `"DESC"`).
 * - `colNameToWidth` (object, defaults to `{}`): maps a column name to a width, for columns with a fixed width.
 * - `rowToShow` (number, defaults to -1): number of the row to display (used for displaying errors)
 *
 * The `sortColumn` and `orderBy` fields of the state always have the same value
 * as the `sortColumn` and `orderBy` props if they are set. This makes it
 * possible to drive the ordering from outside (if they are present), or to let
 * the table handle the ordering itself (if they are missing).
 */
class Table<Row>
    extends React.Component<TableProps<Row>, TableState<Row>>
    implements TableLike
{
    private table: React.RefObject<HTMLDivElement | null>;
    private tbody: React.RefObject<Tbody<Row> | null>;
    private style: React.RefObject<HTMLStyleElement | null>;
    private styleValue: string;
    private selectionHandler: React.RefObject<
        | StandardSelectionHandler<Row>
        | EditCellSelectionHandler<Row>
        | NoopSelectionHandler<Row>
        | null
    >;
    private resizeEventListener: EventListenerOrEventListenerObject | undefined;
    private resizeBeforePrintEventListener:
        | ((this: MediaQueryList, event: MediaQueryListEvent) => void)
        | undefined;
    private getWithRowControlColumn(
        tableMode: TableMode,
        props: TableProps<Row>
    ) {
        let defaultWithRowControlColumn: boolean;
        switch (tableMode.mode) {
            case "EditableWithSelection":
                defaultWithRowControlColumn = true;
                break;
            case "Editable":
                defaultWithRowControlColumn = false;
                break;
            default:
                defaultWithRowControlColumn = false;
                break;
        }
        let withRowControlColumn: boolean;
        if (props.showRowNumbers !== undefined) {
            withRowControlColumn = props.showRowNumbers;
        } else {
            withRowControlColumn = defaultWithRowControlColumn;
        }
        return withRowControlColumn;
    }
    constructor(props: TableProps<Row>) {
        super(props);
        this.table = React.createRef();
        this.tbody = React.createRef();
        this.style = React.createRef();
        this.selectionHandler = React.createRef();
        const realTableMode: TableMode = props.tableMode || { mode: "Simple" };
        this.state = {
            // Hard to determine the factor, but consider a font size of 14px (line height ~19px),
            // a padding of 8px, a border of 1px, that makes 37px per row. Subtract 2px for the
            // scrollbar height to leave enough space at the bottom-right to look almost good.
            verticalScrollHeight: 35 * (props.maxVisibleRows || 0),
            tableMode: realTableMode,
            withRowControlColumn: this.getWithRowControlColumn(
                realTableMode,
                props
            ),
            sortColumn: undefined,
            orderBy: undefined,
            colNameToWidth: {},
            rowToShow: -1,
            sortedRows: [],
            sortedRowNumberToActualRowNumber: null,
            actualRowNumberToSortedRowNumber: null,
        };
        this.styleValue = "";
    }
    componentWillMount() {
        this.resizeEventListener = () => {
            this.updateStylesContent();
        };
        window.addEventListener("resize", this.resizeEventListener);
        this.resizeBeforePrintEventListener = (mediaQueryList) => {
            // Recomputes the table when printing (doesn't work on Firefox)
            if (mediaQueryList.matches) {
                this.updateStylesContent();
            }
        };
        window
            .matchMedia("print")
            .addListener(this.resizeBeforePrintEventListener);
        const colNameToWidth = this.computeColNameToWidth(this.props);
        this.setState({
            sortColumn: this.props.sortColumn,
            orderBy: this.props.orderBy,
            colNameToWidth,
        });
        this.setSortedRows(
            this.props,
            this.props.sortColumn,
            this.props.orderBy
        );
    }
    componentWillUnmount() {
        if (this.resizeEventListener) {
            window.removeEventListener("resize", this.resizeEventListener);
            this.resizeEventListener = undefined;
        }
        if (this.resizeBeforePrintEventListener) {
            window
                .matchMedia("print")
                .removeListener(this.resizeBeforePrintEventListener);
            this.resizeBeforePrintEventListener = undefined;
        }
    }
    componentDidMount() {
        this.updateStylesContent();
    }
    componentWillReceiveProps(newProps: TableProps<Row>) {
        const realTableMode: TableMode = newProps.tableMode || {
            mode: "Simple",
        };
        const withRowControlColumn = this.getWithRowControlColumn(
            realTableMode,
            newProps
        );
        if (
            realTableMode !== this.state.tableMode ||
            withRowControlColumn !== this.state.withRowControlColumn
        ) {
            this.setState({
                tableMode: realTableMode,
                withRowControlColumn,
            });
        }
        let newSortColumn: string | undefined;
        if (
            newProps.sortColumn &&
            newProps.sortColumn !== this.state.sortColumn
        ) {
            newSortColumn = newProps.sortColumn;
        }
        if (newProps.columns !== this.props.columns) {
            const colNameToWidth = this.computeColNameToWidth(newProps);
            if (newProps.sortColumn) {
                newSortColumn = newProps.sortColumn;
            } else if (this.state.sortColumn) {
                newSortColumn = this.state.sortColumn;
            }
            this.setState({ colNameToWidth });
        }
        if (newSortColumn) {
            const sortColumnObject = newProps.columns.filter(
                (c) => c.name === newSortColumn
            )[0];
            if (!sortColumnObject) {
                newSortColumn = newProps.defaultSortColumn;
            }
            this.setState({ sortColumn: newSortColumn });
        }
        let newOrderBy: OrderBy | undefined;
        if (newProps.orderBy && newProps.orderBy !== this.state.orderBy) {
            newOrderBy = newProps.orderBy;
            this.setState({ orderBy: newProps.orderBy });
        }
        let {
            sortedRowNumberToActualRowNumber,
            actualRowNumberToSortedRowNumber,
        } = this.state;
        if (
            newProps.rows !== this.props.rows ||
            newProps.columns !== this.props.columns ||
            newOrderBy ||
            newProps.defaultOrderBy !== this.props.defaultOrderBy ||
            newSortColumn ||
            newProps.defaultSortColumn !== this.props.defaultSortColumn
        ) {
            const orderBy = newOrderBy || this.state.orderBy;
            const sortColumn = newSortColumn || this.state.sortColumn;
            const [
                _sortedRows,
                _sortedRowNumberToActualRowNumber,
                _actualRowNumberToSortedRowNumber,
            ] = this.setSortedRows(newProps, sortColumn, orderBy);
            sortedRowNumberToActualRowNumber =
                _sortedRowNumberToActualRowNumber;
            actualRowNumberToSortedRowNumber =
                _actualRowNumberToSortedRowNumber;
        }
        if (newProps.showFirstError) {
            const firstErrorRow = this.getFirstErrorRow(
                newProps,
                sortedRowNumberToActualRowNumber,
                actualRowNumberToSortedRowNumber
            );
            if (firstErrorRow !== undefined) {
                this.setState({ rowToShow: firstErrorRow });
            }
        }
    }
    componentDidUpdate() {
        const { showFirstError, onFirstErrorShown } = this.props;
        this.updateStylesContent();
        if (showFirstError) {
            // Notify the parent component we have shown the error and reset the internal flag
            if (onFirstErrorShown) {
                onFirstErrorShown();
            }
            if (this.state.rowToShow !== -1) {
                this.setState({ rowToShow: -1 });
            }
        }
    }
    private getFirstErrorRow(
        props: TableProps<Row>,
        sortedRowNumberToActualRowNumber: Array<number> | null,
        actualRowNumberToSortedRowNumber: Array<number> | null
    ): number | undefined {
        const { errors, id } = props;
        // Error ids will have the shape: $id[$rowNumber] or $id[$rowNumber]$colName
        const firstErrorRowSortedIndex = errors
            ? Object.keys(errors)
                  .filter((key) => key.lastIndexOf(id + "[") === 0) // Emulate String.startsWith()
                  .map((key) => {
                      // We can't entirely regexp our way out of it as the id may contain reserved characters
                      const keyMinusPrefix = key.substring(id.length);
                      const rowNumberString = keyMinusPrefix.replace(
                          /^\[(\d+)\].*/,
                          "$1"
                      );
                      const actualRowNumber = parseInt(rowNumberString, 10);
                      const sortedRowNumber = actualRowNumberToSortedRowNumber
                          ? actualRowNumberToSortedRowNumber[actualRowNumber]
                          : actualRowNumber;
                      return sortedRowNumber;
                  })
                  .sort((a, b) => a - b)[0]
            : undefined;
        return firstErrorRowSortedIndex !== undefined &&
            sortedRowNumberToActualRowNumber
            ? sortedRowNumberToActualRowNumber[firstErrorRowSortedIndex]
            : firstErrorRowSortedIndex;
    }
    shouldComponentUpdate(
        nextProps: TableProps<Row>,
        nextState: TableState<Row>
    ) {
        const propsStyles: TableStyles =
            this.props.styles || DefaultTableStyles;
        const nextPropsStyles: TableStyles =
            nextProps.styles || DefaultTableStyles;
        const stylesDifferFunction: () => boolean = () => {
            const differ = Object.keys(DefaultTableStyles).some(
                (key) =>
                    (propsStyles as any)[key] !== (nextPropsStyles as any)[key]
            );
            return differ;
        };
        return (
            this.props.columns !== nextProps.columns ||
            this.props.rows !== nextProps.rows ||
            this.props.tableMode !== nextProps.tableMode ||
            stylesDifferFunction() ||
            this.props.sortType !== nextProps.sortType ||
            this.props.sortColumn !== nextProps.sortColumn ||
            this.props.orderBy !== nextProps.orderBy ||
            this.props.errors !== nextProps.errors ||
            this.props.errorMapping !== nextProps.errorMapping ||
            this.state.sortColumn !== nextState.sortColumn ||
            this.state.orderBy !== nextState.orderBy ||
            this.state.rowToShow !== nextState.rowToShow
        );
    }
    render() {
        const {
            id,
            columns,
            onCellPaste,
            onNewRowCellChanged,
            onActiveCellChanged,
            onCellChanged,
            errors,
            errorMapping,
            rowRenderer,
            buildEmptyRow,
            getCellClassName,
            onDeleteCells,
            getCopyable,
            maxVisibleRows,
        } = this.props;
        const {
            tableMode,
            withRowControlColumn,
            rowToShow,
            sortedRows,
            sortedRowNumberToActualRowNumber,
            actualRowNumberToSortedRowNumber,
            verticalScrollHeight,
        } = this.state;

        const realStyles: TableStyles = this.props.styles || DefaultTableStyles;

        const sortColumn = this.getSortColumn(this.props);
        const orderBy = this.getOrderBy(this.props);
        let SelectionHandler: React.ComponentClass<SelectionHandlerProps<Row>>;
        switch (tableMode.mode) {
            case "EditableWithSelection":
                SelectionHandler = StandardSelectionHandler;
                break;
            case "Editable":
                SelectionHandler = EditCellSelectionHandler;
                break;
            default:
                SelectionHandler = NoopSelectionHandler;
                break;
        }
        const tableId = id !== null ? id : "table";
        const actualRowRenderer = rowRenderer
            ? rowRenderer
            : defaultRowRenderer;
        return (
            <div className={theme.reactableContainer}>
                <div
                    id={id}
                    className={
                        theme.reactable + " " + realStyles.tableClassName
                    }
                    ref={this.table}
                >
                    {this.buildStyles()}
                    <Theader
                        key="theader"
                        columns={columns}
                        sortColumn={sortColumn}
                        orderBy={orderBy}
                        onSort={this.onSortResolver(this.props)}
                        withRowControlColumn={withRowControlColumn}
                        styles={realStyles}
                    />
                    <SelectionHandler
                        ref={this.selectionHandler}
                        columns={columns}
                        rows={sortedRows}
                        styles={realStyles}
                        onActiveCellChanged={onActiveCellChanged}
                        canAddRow={onNewRowCellChanged !== undefined}
                        onDeleteCells={onDeleteCells}
                        onCellPaste={onCellPaste}
                        getCellClassName={getCellClassName}
                        getCopyable={getCopyable}
                        activeRow={rowToShow}
                        actualRowNumberToSortedRowNumber={
                            actualRowNumberToSortedRowNumber
                        }
                        sortedRowNumberToActualRowNumber={
                            sortedRowNumberToActualRowNumber
                        }
                    >
                        <Tbody
                            ref={this.tbody}
                            tableId={tableId}
                            columns={columns}
                            rows={sortedRows}
                            sortedRowNumberToActualRowNumber={
                                sortedRowNumberToActualRowNumber
                            }
                            actualRowNumberToSortedRowNumber={
                                actualRowNumberToSortedRowNumber
                            }
                            verticalScrollHeight={verticalScrollHeight}
                            onNewRowCellChanged={onNewRowCellChanged}
                            errors={errors}
                            errorMapping={errorMapping}
                            rowRenderer={actualRowRenderer}
                            buildEmptyRow={buildEmptyRow}
                            maxVisibleRows={maxVisibleRows}
                            withRowControlColumn={withRowControlColumn}
                            onCellChanged={onCellChanged}
                            onHotkey={/*snc*/ emptyFn}
                            styles={realStyles}
                        />
                    </SelectionHandler>
                </div>
            </div>
        );
    }
    private onSortResolver = createSelector(
        (props: TableProps<Row>) => props.sortType,
        (props: TableProps<Row>) => props.onSortChanged,
        (
            sortType,
            onSortChanged
        ): ((colName: string, orderBy: OrderBy) => void) | null => {
            switch (sortType) {
                case "manual":
                    return (colName, orderBy) =>
                        this.handleSort(colName, orderBy);
                case "external":
                    return onSortChanged || null;
                default:
                    return null;
            }
        }
    );
    private getSortColumn(props: TableProps<Row>): string | undefined {
        return this.state.sortColumn
            ? this.state.sortColumn
            : props.defaultSortColumn;
    }
    private getDefaultPropsOrderBy(props: TableProps<Row>): OrderBy {
        return props.defaultOrderBy ? props.defaultOrderBy : OrderBy.ASC;
    }
    private getOrderBy(props: TableProps<Row>): OrderBy {
        return this.state.orderBy
            ? this.state.orderBy
            : this.getDefaultPropsOrderBy(props);
    }
    private buildStyles(): React.ReactNode {
        // Build an empty style element, it will be filled by updateStylesContent()
        // whenever the table is updated or resized
        return <style ref={this.style} />;
    }
    private isScrollVertically(): boolean {
        const { maxVisibleRows, rows } = this.props;
        return !!(maxVisibleRows && rows.length > maxVisibleRows);
    }
    private getViewWidth(): number {
        if (!this.tbody.current) {
            return 0;
        }
        const scrollVertically = this.isScrollVertically();
        const parentEltWidth =
            this.tbody.current.getViewWidth() -
            (scrollVertically ? SCROLLBAR_V_WIDTH : 0);
        return parentEltWidth;
    }
    private updateStylesContent(): void {
        const { id, columns } = this.props;
        const { withRowControlColumn, verticalScrollHeight } = this.state;
        const viewWidth = this.getViewWidth();
        const actualColNameToWidth =
            this.computeActualColNameToWidth(viewWidth);
        const widths: Array<number> = Object.values(actualColNameToWidth);
        const columnsWidth =
            widths.reduce((acc: number, width: number) => acc + width, 0) +
            (withRowControlColumn ? ROW_CONTROLS_WIDTH_IN_PX : 0);
        const minColumnsWidth =
            widths.reduce(
                (acc: number, width: number) =>
                    acc + Math.min(width, MIN_WIDTH_PER_COLUMN),
                0
            ) + (withRowControlColumn ? ROW_CONTROLS_WIDTH_IN_PX : 0);
        const scrollVertically = this.isScrollVertically();
        const totalWidth =
            RIGHT_SIDE_BORDER_WIDTH +
            columnsWidth +
            (scrollVertically ? SCROLLBAR_V_WIDTH : 0);
        const escapedId = CSS.escape(id);
        const styles: Array<string> = [];
        // Prevent the table from going crazy due to external widgets which set the page width to a large value.
        // E.g. the collect sticky header.
        styles.push(
            `[id='${escapedId}'] .${theme.thead} { min-width: ${minColumnsWidth}px; max-width: ${totalWidth}px }`
        );
        if (scrollVertically) {
            styles.push(
                `[id='${escapedId}'] .${theme.thead} .${theme.headerScrollbarCell} { width: ${SCROLLBAR_V_WIDTH}px; }`
            );
        }
        const formatRule = (
            selectors: Array<string>,
            properties: Array<string>
        ) => selectors.join(", ") + " {" + properties.join(";\n") + "}";
        columns.forEach((col) => {
            const width = actualColNameToWidth[col.name];
            const escapedColName = CSS.escape(col.name);
            const selectors = [
                "[id='" +
                    escapedId +
                    "'] .col-" +
                    escapedColName +
                    "." +
                    theme.th,
                "[id='" +
                    escapedId +
                    "'] .col-" +
                    escapedColName +
                    "." +
                    theme.td,
            ];
            const properties = [
                "width: " + width.toString() + "px",
                "max-width: " + width.toString() + "px",
                "min-width: " + width.toString() + "px",
            ];
            const rule = formatRule(selectors, properties);
            styles.push(rule);
        });
        const additionalRowClassNames =
            (this.props.styles && this.props.styles.additionalRowClassNames) ||
            undefined;
        if (additionalRowClassNames && additionalRowClassNames.length > 0) {
            additionalRowClassNames.forEach((className) => {
                styles.push(
                    "[id='" +
                        escapedId +
                        "'] ." +
                        theme.tr +
                        "." +
                        className +
                        " { width: " +
                        totalWidth +
                        "px; max-width: " +
                        totalWidth +
                        "px; min-width: " +
                        totalWidth +
                        "px; }"
                );
            });
        }
        if (withRowControlColumn) {
            const selectors = ["#" + escapedId + " ." + theme.rowControlColumn];
            const properties = [
                "width: " + ROW_CONTROLS_WIDTH_IN_PX + "px",
                "min-width: " + ROW_CONTROLS_WIDTH_IN_PX + "px",
                "max-width: " + ROW_CONTROLS_WIDTH_IN_PX + "px",
            ];
            styles.push(formatRule(selectors, properties));
        }
        if (this.isScrollVertically()) {
            styles.push(
                "[id='" +
                    escapedId +
                    "'] ." +
                    theme.tbodyVerticalScroll +
                    " { display: block; overflow-y: scroll; overflow-x: hidden; min-height: " +
                    verticalScrollHeight +
                    "px; max-height: " +
                    verticalScrollHeight +
                    "px; }"
            );
        }
        const scrollHorizontally = !(viewWidth >= totalWidth);
        if (this.tbody.current) {
            this.tbody.current.setScrollHorizontally(scrollHorizontally);
        }
        if (scrollHorizontally) {
            styles.push(
                "[id='" +
                    escapedId +
                    "'] ." +
                    theme.tbodyWrapper +
                    " { overflow-x: scroll; }"
            );
        }
        styles.push(
            `[id='${escapedId}'] .${theme.rowsContainer} { width: ${columnsWidth}px; }`
        );
        const newStyle = styles.join("\n");
        if (this.style.current && newStyle !== this.styleValue) {
            this.style.current.innerHTML = this.styleValue = newStyle;
        }
    }
    private computeActualColNameToWidth(viewWidth: number): ColNameToWidth {
        const { columns } = this.props;
        const { withRowControlColumn, colNameToWidth } = this.state;
        // We don't want the table to take less than this width, even if we have to display horizontal scrollbars
        const colNamesWithFixedWidth = Object.keys(colNameToWidth);
        let fixedWidthColSize = 0;
        colNamesWithFixedWidth.forEach((colName) => {
            fixedWidthColSize += colNameToWidth[colName];
        });
        if (withRowControlColumn) {
            fixedWidthColSize += ROW_CONTROLS_WIDTH_IN_PX;
        }
        if (!this.table.current) {
            throw new Error("No table");
        }
        const table = this.table.current;
        const invisibleColumns: { [columnName: string]: boolean } = {};
        const headers = table.querySelectorAll("." + theme.th);
        columns.forEach((col) => {
            const colHeaderCssSelector = CSS.escape("col-" + col.name);
            const header = Object.values(headers).find((h) => {
                // Ignore space, the column name should already be enough
                // to match correctly
                if (h.className.indexOf(colHeaderCssSelector) >= 0) {
                    return true;
                } else {
                    return false;
                }
            });
            if (
                header &&
                (header.clientWidth <= 0 || header.clientHeight <= 0)
            ) {
                invisibleColumns[col.name] = true;
            }
        });
        const colsWithoutFixedWidth = columns.filter(
            (col) =>
                colNameToWidth[col.name] === undefined &&
                invisibleColumns[col.name] === undefined
        );
        const minWidthForNonFixedColumns = colsWithoutFixedWidth.reduce(
            (totalWidth, col) => {
                const minWidth = (col as MinWidthColumn<Row, any, any, any>)
                    .minWidth;
                return (
                    totalWidth +
                    (minWidth !== undefined ? minWidth : MIN_WIDTH_PER_COLUMN)
                );
            },
            0
        );
        const minTableWidth = fixedWidthColSize + minWidthForNonFixedColumns;
        const actualTableWidth = Math.max(viewWidth, minTableWidth);

        /* to keep room for the right side border */
        const extraWidth =
            actualTableWidth - RIGHT_SIDE_BORDER_WIDTH - minTableWidth;
        const colCountWithoutFixedWidth = colsWithoutFixedWidth.length;
        const extraWidthForNonFixedColumns =
            colCountWithoutFixedWidth > 0 && extraWidth > 0
                ? Math.floor(extraWidth / colCountWithoutFixedWidth)
                : 0;
        // Cater to rounding errors
        let remainingWidth =
            extraWidth -
            extraWidthForNonFixedColumns * colCountWithoutFixedWidth;
        if (remainingWidth <= 0) {
            remainingWidth = 0;
        }
        const result: ColNameToWidth = {};
        columns.forEach((col, index) => {
            let colWidth: number | undefined = colNameToWidth[col.name];
            if (colWidth === undefined) {
                if (invisibleColumns[col.name] === true) {
                    colWidth = 0;
                } else {
                    colWidth = (col as MinWidthColumn<Row, any, any, any>)
                        .minWidth;
                    if (colWidth === undefined) {
                        colWidth = MIN_WIDTH_PER_COLUMN;
                    }
                    colWidth += extraWidthForNonFixedColumns;
                }
            }
            if (index === 0) {
                // Add the reminding width on the first column, if any
                colWidth += remainingWidth;
            }
            result[col.name] = colWidth;
        });
        return result;
    }
    private computeColNameToWidth(props: TableProps<Row>): ColNameToWidth {
        const { columns } = props;
        const colNameToWidth: ColNameToWidth = {};
        columns.forEach((col) => {
            const fixedWidth = (col as FixedWidthColumn<Row, any, any, any>)
                .fixedWidth;
            if (fixedWidth) {
                colNameToWidth[col.name] = fixedWidth;
            }
        });
        return colNameToWidth;
    }
    private setSortedRows(
        props: TableProps<Row>,
        sortColumn: string | undefined,
        orderBy: OrderBy | undefined
    ): [Array<Row>, Array<number> | null, Array<number> | null] {
        const [sortedRows, sortedRowNumberToActualRowNumber] =
            this.computeSortedRows(props, sortColumn, orderBy);
        const actualRowNumberToSortedRowNumber =
            sortedRowNumberToActualRowNumber
                ? invertNumericArray(sortedRowNumberToActualRowNumber)
                : null;
        this.setState({
            sortedRows,
            sortedRowNumberToActualRowNumber,
            actualRowNumberToSortedRowNumber,
        });
        return [
            sortedRows,
            sortedRowNumberToActualRowNumber,
            actualRowNumberToSortedRowNumber,
        ];
    }
    private computeSortedRows(
        props: TableProps<Row>,
        sortColumn: string | undefined,
        orderBy: OrderBy | undefined
    ): [Array<Row>, Array<number> | null] {
        if (props.sortType === "external") {
            return [props.rows, null];
        }
        const defaultPropsOrderBy = this.getDefaultPropsOrderBy(props);
        const rows = props.rows;
        let sortedRows: Array<Row>;
        let sortedRowNumberToActualRowNumber: Array<number> | null;
        if (sortColumn) {
            if (
                sortColumn !== props.defaultSortColumn ||
                orderBy !== defaultPropsOrderBy
            ) {
                const sortedRowAndIndexes = rows.map((row, index) => {
                    return { row, index };
                });
                const colObject = props.columns.filter(
                    (c) => c.name === sortColumn
                )[0];
                const sortDataFunction: (row: Row, rowNumber: number) => any =
                    colObject.getSortData
                        ? colObject.getSortData
                        : colObject.getData;
                let compare = Cmp.map<{ row: Row; index: number }, any>(
                    (rowAndIndex) =>
                        sortDataFunction(rowAndIndex.row, rowAndIndex.index),
                    Cmp.undefinedFirst()
                );
                const { getDefaultSortData } = props;
                if (getDefaultSortData) {
                    compare = Cmp.chain([
                        compare,
                        Cmp.map(
                            (rowAndIndex) =>
                                getDefaultSortData(
                                    rowAndIndex.row,
                                    rowAndIndex.index
                                ),
                            Cmp.undefinedFirst()
                        ),
                    ]);
                }
                sortedRowAndIndexes.sort(
                    orderBy === OrderBy.ASC ? compare : Cmp.inverse(compare)
                );
                sortedRows = sortedRowAndIndexes.map(
                    (rowAndIndex) => rowAndIndex.row
                );
                sortedRowNumberToActualRowNumber = [];
                sortedRowAndIndexes.forEach((rowAndIndex, sortedIndex) => {
                    const originalIndex = rowAndIndex.index;
                    sortedRowNumberToActualRowNumber![sortedIndex] =
                        originalIndex;
                });
            } else {
                sortedRows = rows;
                sortedRowNumberToActualRowNumber = null;
            }
        } else {
            sortedRows = rows;
            sortedRowNumberToActualRowNumber = null;
        }
        return [sortedRows, sortedRowNumberToActualRowNumber];
    }
    private handleSort(colName: string, orderBy: OrderBy): void {
        this.setState({ sortColumn: colName, orderBy });
        this.setSortedRows(this.props, colName, orderBy);
        if (this.props.onSortChanged) {
            this.props.onSortChanged(colName, orderBy);
        }
    }

    copyAllToClipboard() {
        return this.copyAreaToClipboard(
            this.props.columns.map((col) => col.name),
            range(this.props.rows.length)
        );
    }
    copyAreaToClipboard(colNames: Array<string>, rowNumbers: Array<number>) {
        if (!this.props.getCopyable) {
            return;
        }
        const copyable = this.props.getCopyable(colNames, rowNumbers);
        if (copyable !== null) {
            copyDataToClipboard(copyable);
        }
    }
    copySelectionToClipboard() {
        if (!this.selectionHandler.current) {
            throw new Error("No selection handler");
        }
        if (!this.props.getCopyable) {
            throw new Error("No getCopyable");
        }
        const selection = this.selectionHandler.current.getSelection();
        if (selection) {
            const copyable = this.props.getCopyable(
                selection.colNames,
                selection.rowNumbers
            );
            if (copyable !== null) {
                copyDataToClipboard(copyable);
            }
        }
    }
}

type TableStyles = {
    tableClassName: string;
    additionalRowClassNames?: Array<string>;
} & TheaderStyles &
    TbodyStyles &
    SelectionHandlerStyles;

const DefaultTableStyles: TableStyles = {
    // Table
    tableClassName: theme.tableDefault + " " + theme.sortableTable,
    // Thead
    headerClassName: "",
    // Tr in header
    headerRowClassName: "",
    // Td in header
    headerCellClassName: "",
    // Tbody
    bodyClassName: "",
    // Tr
    rowClassName: "",
    // Td
    cellClassName: "",
    // Selection Handler
    activeCellClassName: theme.activeCell,
    editedCellClassName: theme.editedCell,
    selectedCellClassName: theme.selectedCell,
    anchorCellClassName: theme.anchorCell,
    topSelectionClassName: theme.topSelection,
    bottomSelectionClassName: theme.bottomSelection,
    leftSelectionClassName: theme.leftSelection,
    rightSelectionClassName: theme.rightSelection,
};

const DefaultEditTableStyles: TableStyles = set(DefaultTableStyles, {
    tableClassName:
        theme.tableDefault + " " + theme.sortableTable + " " + theme.editTable,
});

interface SimpleTableMode {
    mode: "Simple";
}

interface EditableTableMode {
    mode: "Editable";
}

interface EditableWithSelectionTableMode {
    mode: "EditableWithSelection";
}

type TableMode =
    | SimpleTableMode
    | EditableTableMode
    | EditableWithSelectionTableMode;

interface TableProps<Row> {
    id: string;
    columns: Array<Column<Row, any, any, any>>;
    rows: Array<Row>;
    getRowKey?: (row: Row) => string;
    sortColumn?: string;
    orderBy?: OrderBy;
    sortType?: "manual" | "external";
    getDefaultSortData?: (row: Row, rowNumber: number) => any;
    defaultSortColumn?: string;
    defaultOrderBy?: OrderBy;
    onSortChanged?: (sortColumn: string, orderBy: OrderBy) => void;
    styles?: TableStyles;
    tableMode?: TableMode;
    errors?: ValidationErrors;
    errorMapping?: ErrorMapping;
    showFirstError?: boolean;
    showRowNumbers?: boolean;
    onFirstErrorShown?: () => void;
    rowRenderer?: RowRenderer<Row>;
    onDeleteCells?: (
        colNames: Array<string>,
        rowNumbers: Array<number>
    ) => void;
    onActiveCellChanged?: (colName: string, rowNumber: number) => void;
    buildEmptyRow?: (columns: Array<Column<Row, any, any, any>>) => Row;
    onNewRowCellChanged?: (
        colName: string,
        rowNumber: number,
        newData: any
    ) => void;
    onCellPaste?: (
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ) => void;
    getCellClassName?: (
        colName: string,
        rowNumber: number
    ) => string | undefined;
    onCellChanged?: (colName: string, rowNumber: number, newData: any) => void;
    maxVisibleRows?: number;
    getCopyable?: (
        colNames: Array<string>,
        rowNumbers: Array<number>
    ) => string | null;
}

interface TableState<Row> {
    verticalScrollHeight: number;
    tableMode: TableMode;
    withRowControlColumn: boolean;
    sortedRows: Array<Row>;
    sortedRowNumberToActualRowNumber: Array<number> | null;
    actualRowNumberToSortedRowNumber: Array<number> | null;
    sortColumn?: string;
    orderBy?: OrderBy;
    colNameToWidth: ColNameToWidth;
    rowToShow?: number;
}

// Given an array of numbers representing positions, returns its invert
function invertNumericArray(arr: Array<number>): Array<number> {
    const result = arr.slice();
    for (let i = 0; i < arr.length; i++) {
        result[arr[i]] = i;
    }
    return result;
}

/*
 * Copyable tools
 */

type GetRawTableCopyableFunction = (
    colNames: Array<string>,
    rowNumbers: Array<number>,
    includeHeaderRow: boolean
) => ExportableArray;

function makeGetRawTableCopyable<Row, Data, SortableData, RawData>(
    columns: Array<Column<Row, Data, SortableData, RawData>>,
    rows: Array<Row>
): GetRawTableCopyableFunction {
    const columnsByName: {
        [colName: string]: Column<Row, Data, SortableData, RawData>;
    } = {};
    columns.forEach((col) => {
        columnsByName[col.name] = col;
    });
    return (colNames, rowNumbers, includeHeaderRow) => {
        const copyableColumns = colNames.map((name) => columnsByName[name]);
        const dataToCopy: Array<Array<string | number | null>> = rowNumbers.map(
            (i) => {
                const row = rows[i];
                return copyableColumns.map((col) => {
                    if (col.getCopyableData) {
                        return col.getCopyableData(row, i);
                    } else {
                        const data = col.getData(row, i);
                        return data !== undefined && data !== null
                            ? data.toString()
                            : null;
                    }
                });
            }
        );
        return {
            header: includeHeaderRow
                ? copyableColumns.map((col) => col.label)
                : undefined,
            content: dataToCopy,
        };
    };
}

type GetTableCopyableFunction = (
    colNames: Array<string>,
    rowNumbers: Array<number>,
    includeHeaderRow: boolean,
    quotes: boolean,
    delimiter: string,
    newline: string
) => string;

function makeGetTableCopyable<
    Row,
    Data extends { toString(): string },
    SortableData,
    RawData,
>(
    columns: Array<Column<Row, Data, SortableData, RawData>>,
    rows: Array<Row>
): GetTableCopyableFunction {
    const getRawTableCopyable = makeGetRawTableCopyable(columns, rows);
    return (
        colNames,
        rowNumbers,
        includeHeaderRow,
        quotes,
        delimiter,
        newline
    ) => {
        const exportableArray = getRawTableCopyable(
            colNames,
            rowNumbers,
            includeHeaderRow
        );
        const dataWithHeader = exportableArray.header
            ? [
                  exportableArray.header as Array<
                      string | number | null | undefined
                  >,
              ].concat(exportableArray.content)
            : exportableArray.content;
        return Papa.unparse(dataWithHeader, { quotes, delimiter, newline });
    };
}
export type {
    RowRenderer,
    TableLike,
    TableProps,
    TableStyles,
    RowRendererParams,
    GetRawTableCopyableFunction,
    GetTableCopyableFunction,
};
export {
    OrderBy,
    Column,
    FixedWidthColumn,
    MinWidthColumn,
    Editor,
    EditorProps,
    Table,
    Blurable,
    defaultRowRenderer,
    makeGetRawTableCopyable,
    makeGetTableCopyable,
    theme as TableTheme,
    DefaultTableStyles,
    DefaultEditTableStyles,
};
